<template>
  <!-- URGENT SYSTEM MAINTENANCE MESSAGE -->
  <div v-if="showSystemMaintenance" class="system-maintenance-overlay">
    <div class="maintenance-message">
      <div class="maintenance-icon">
        <i class="fas fa-tools"></i>
      </div>
      <h1>System Maintenance</h1>
      <p>We're currently performing scheduled maintenance to improve your experience.</p>
      <p class="maintenance-subtitle">We'll be back shortly. Thank you for your patience.</p>
      <div class="maintenance-spinner">
        <div class="spinner"></div>
      </div>
    </div>
  </div>

  <div v-if="isReady" class="app-wrapper min-h-screen flex flex-col">
    <!-- Add DynamicDialogs at the root level -->
    <DynamicDialogs />
    
    <nav v-if="auth.isInitialized && !isLoading && !hideNav"
      class="navbar"
      :class="{ 'login-page': isLoginView && router.currentRoute.value.path === '/login', 'forgot-password-page': isLoginView && router.currentRoute.value.path === '/forgot-password', 'register-page': isLoginView && router.currentRoute.value.path === '/register' }"
      :data-theme="themeStore.isDark ? 'dark' : 'light'">
      <!-- Logo with Company Dropdown (authenticated with multiple companies) -->
      <div v-if="auth.isAuthenticated && auth.user?.hasMultipleCompanyAccess" class="logo-container">
        <div class="logo" @click="toggleCompanySwitchDropdown">
          <img
            :src="BRAND_LOGO_URL"
            :alt="BRAND_LOGO_ALT"
            class="light-logo"
          />
          <img :src="BRAND_LOGO_DARK_URL" :alt="BRAND_LOGO_ALT" class="dark-logo" />
        </div>
        
        <!-- Company Dropdown Menu -->
        <div
          v-show="isCompanySwitchDropdownOpen"
          class="company-switch-menu"
          @click.stop=""
        >
          <div class="company-menu-header">
            <div class="menu-header">
              <i class="fas fa-building menu-header-icon"></i>
              <span>Switch Company</span>
            </div>
          </div>
          <div class="company-menu-content">
            <div v-if="isLoadingCompanies" class="company-menu-loading">
              <i class="fas fa-spinner fa-spin"></i>
              <span>Loading companies...</span>
            </div>
            <template v-else>
              <button
                v-for="company in companies"
                :key="company.id"
                @click="handleCompanySwitch(company)"
                class="company-menu-item"
                :class="{'company-menu-item-active': currentCompanyId === String(company.id) || company.isSelected}"
                :disabled="currentCompanyId === String(company.id) || company.isSelected"
              >
                <div class="company-menu-item-content">
                  <div class="company-menu-item-icon-wrapper">
                    <i class="fas fa-building"></i>
                  </div>
                  <div class="company-menu-item-text">
                    <span class="company-menu-item-name">{{ company.name }}</span>
                    <span v-if="company.nmls" class="company-menu-item-nmls">NMLS: {{ company.nmls }}</span>
                  </div>
                </div>
                <div v-if="currentCompanyId === String(company.id) || company.isSelected" class="company-menu-item-check">
                  <i class="fas fa-check"></i>
                </div>
              </button>
              <div v-if="companies.length === 0" class="company-menu-empty">
                <i class="fas fa-inbox"></i>
                <span>No companies available</span>
              </div>
            </template>
          </div>
        </div>
      </div>
      
      <!-- Regular Logo Link (authenticated without multiple companies or not authenticated) -->
      <router-link v-else to="/" class="logo">
        <img
          :src="BRAND_LOGO_URL"
          :alt="BRAND_LOGO_ALT"
          class="light-logo"
        />
        <img :src="BRAND_LOGO_DARK_URL" :alt="BRAND_LOGO_ALT" class="dark-logo" />
      </router-link>

      <!-- Desktop Navigation -->
      <div class="desktop-nav">
        <template v-if="auth.isAuthenticated">
          <router-link to="/union-home-mortgage-loans" class="nav-item">Union Home Mortgage Loans</router-link>
          <router-link to="/dashboard" class="nav-item">Dashboard</router-link>
          <router-link v-if="tpoPortalRoleStore.getCurrentRole() === PortalRoleType.BROKER && !multipleCompanyAccessStore.getHasMultipleCompanyAccess()" to="/loans" class="nav-item">Pipeline</router-link>
          <router-link v-if="tpoPortalRoleStore.getCurrentRole() === PortalRoleType.BROKER && multipleCompanyAccessStore.getHasMultipleCompanyAccess()" to="/multiple-company-access-pipeline" class="nav-item">Pipeline</router-link>
          <router-link v-if="tpoPortalRoleStore.getCurrentRole() === PortalRoleType.PROCESSOR_MANAGER" to="/team-loans" class="nav-item">Team Pipeline</router-link>
          <!-- <router-link to="/loan-applications" class="nav-item">Apps</router-link> -->
          
          <!-- Company Dropdown - Redesigned -->
          <div v-if="isCorporate" class="nav-item-dropdown d-none">
            <button 
              @click="toggleCompanyDropdown" 
              :class="['nav-dropdown-trigger', {'active': isCompanyDropdownOpen || isCompanyActive}]"
            >
              <span>Company</span>
              <i class="fas fa-chevron-down transition-transform"
                :class="{'rotate-180': isCompanyDropdownOpen}"
              ></i>
            </button>
            
            <!-- Dropdown Menu -->
            <div
              v-show="isCompanyDropdownOpen"
              class="nav-dropdown-menu"
              @click.stop=""
            >
              <div class="menu-header">Company Options</div>
              <div class="menu-divider"></div>
              <router-link 
                v-if="isCorporate" 
                to="/members" 
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-users menu-icon"></i>
                <span>Member</span>
              </router-link>
            </div>
          </div>
          
          <!-- New Loan Dropdown -->
          <div class="nav-item-dropdown">
            <button 
              @click="toggleNewLoanDropdown" 
              :class="['nav-dropdown-trigger', {'active': isNewLoanDropdownOpen || isNewLoanActive}]"
            >
              <span>New Loan</span>
              <i class="fas fa-chevron-down transition-transform"
                :class="{'rotate-180': isNewLoanDropdownOpen}"
              ></i>
            </button>
            
            <!-- Dropdown Menu -->
            <div
              v-show="isNewLoanDropdownOpen"
              class="nav-dropdown-menu"
              @click.stop=""
            >
              <div class="menu-header">Create New Loan</div>
              <div class="menu-divider"></div>
              <router-link 
                to="/loan-application/create" 
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-plus-circle menu-icon"></i>
                <span>Create From Scratch</span>
              </router-link>
              <router-link
                v-if="showMismoUploadClassic"
                to="/loan-application/upload"
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-upload menu-icon"></i>
                <span>Upload MISMO file</span>
              </router-link>
              <router-link
                v-if="showMismoUploadPrepare"
                to="/loan-application/upload-prepare"
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-upload menu-icon"></i>
                <span>Upload MISMO file</span>
              </router-link>
              <router-link
                to="/resources/guide"
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-book-open menu-icon"></i>
                <span>Guide - Create a Loan</span>
              </router-link>
            </div>
          </div>
          
          <!-- Pricing Dropdown -->
          <div class="nav-item-dropdown">
            <button
              @click="togglePricingDropdown"
              :class="['nav-dropdown-trigger', {'active': isPricingDropdownOpen || isPricingActive}]"
            >
              <span>Pricing</span>
              <i class="fas fa-chevron-down transition-transform"
                :class="{'rotate-180': isPricingDropdownOpen}"
              ></i>
            </button>

            <!-- Dropdown Menu -->
            <div
              v-show="isPricingDropdownOpen"
              class="nav-dropdown-menu"
              @click.stop=""
            >
              <div class="menu-header">Pricing</div>
              <div class="menu-divider"></div>
              <router-link
                to="/quick-pricer-menu"
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-calculator menu-icon"></i>
                <span>Quick Pricer</span>
              </router-link>
              <router-link
                to="/scenario-desk"
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-chart-line menu-icon"></i>
                <span>Pricing Scenario</span>
              </router-link>
              <div class="menu-item disabled">
                <i class="fas fa-dollar-sign menu-icon"></i>
                <span>Price A Loan <em>(Coming soon)</em></span>
              </div>
            </div>
          </div>
          
          <!-- Tools Dropdown -->
          <div class="nav-item-dropdown">
            <button 
              @click="toggleToolsDropdown" 
              :class="['nav-dropdown-trigger', {'active': isToolsDropdownOpen || isToolsActive}]"
            >
              <span>Tools</span>
              <i class="fas fa-chevron-down transition-transform"
                :class="{'rotate-180': isToolsDropdownOpen}"
              ></i>
            </button>
            
            <!-- Dropdown Menu -->
            <div
              v-show="isToolsDropdownOpen"
              class="nav-dropdown-menu"
              @click.stop=""
            >
              <div class="menu-header">Tools</div>
              <div class="menu-divider"></div>
              <router-link 
                to="/tools/how-to-videos" 
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-video menu-icon"></i>
                <span>How-to Videos</span>
              </router-link>
            </div>
          </div>
          
          <!-- Resources Dropdown -->
          <div class="nav-item-dropdown">
            <button 
              @click="toggleResourcesDropdown" 
              :class="['nav-dropdown-trigger', {'active': isResourcesDropdownOpen || isResourcesActive}]"
            >
              <span>Resources</span>
              <i class="fas fa-chevron-down transition-transform"
                :class="{'rotate-180': isResourcesDropdownOpen}"
              ></i>
            </button>
            
            <!-- Dropdown Menu -->
            <div
              v-show="isResourcesDropdownOpen"
              class="nav-dropdown-menu"
              @click.stop=""
            >
              <div class="menu-header">Resources</div>
              <div class="menu-divider"></div>
              <router-link 
                to="/resources" 
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-home menu-icon"></i>
                <span>Resources Home</span>
              </router-link>
              <router-link 
                to="/resources/exclusionary-list" 
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-ban menu-icon"></i>
                <span>Exclusionary List</span>
              </router-link>
              <router-link 
                to="/resources/appraisal" 
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-chart-line menu-icon"></i>
                <span>Appraisal</span>
              </router-link>
              <router-link 
                to="/resources/programs" 
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-list-alt menu-icon"></i>
                <span>Programs</span>
              </router-link>
              <router-link 
                to="/resources/forms" 
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-file-alt menu-icon"></i>
                <span>Forms</span>
              </router-link>
              <router-link 
                to="/resources/bank-statements-submission" 
                class="menu-item coming-soon-nav"
                @click="closeAllDropdowns"
                style="opacity:0.5; pointer-events:none;"
              >
                <i class="fas fa-university menu-icon"></i>
                <span>
                  Bank Statement Submission
                  <span class="coming-soon-badge">Coming Soon</span>
                </span>
              </router-link>
              <div class="menu-item coming-soon-nav" style="opacity:0.5; pointer-events:none;">
                <i class="fas fa-calculator menu-icon"></i>
                <div>
                  <span>Calculators</span>
                  <span class="coming-soon-badge">Coming Soon</span>
                </div>
              </div>
              <router-link 
                to="/resources/mortgage-clause" 
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-file-contract menu-icon"></i>
                <span>Mortgagee Clause</span>
              </router-link>
              <router-link 
                to="/resources/wholesale-turntimes" 
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-clock menu-icon"></i>
                <span>Wholesale Turn Times</span>
              </router-link>
              <router-link 
                to="/resources/licensing" 
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-id-card menu-icon"></i>
                <span>Licensing</span>
              </router-link>
              <router-link 
                to="/resources/guide" 
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-book-open menu-icon"></i>
                <span>Guide</span>
              </router-link>
            </div>
          </div>

          <!-- TPOP Agent (single link; include loanId when on loan detail) -->
          <router-link
            :to="tpopAgentLink"
            class="nav-item"
            :class="{ 'active': isTpopAgentActive }"
          >
            TPOP Agent
          </router-link>

          <!-- PortalMode Dropdown -->
          <div class="nav-item-dropdown portal-mode-dropdown" v-if="!portalModeStore.isFetchingPortalMode && portalModeStore.hasMultiplePortalModes">
            <button 
              @click="togglePortalModeDropdown" 
              :class="['nav-dropdown-trigger portal-mode-trigger', {'active': isPortalModeDropdownOpen || isPortalModeActive}]"
            >
              <span>{{ MeridianPortalModeTypeToPortalModeType(portalModeStore.currentSelection as MeridianPortalModeType) }}</span>
              <i class="fas fa-chevron-down transition-transform"
                :class="{'rotate-180': isPortalModeDropdownOpen}"
              ></i>
            </button>
            
            <!-- Dropdown Menu -->
            <div
              v-show="isPortalModeDropdownOpen"
              class="nav-dropdown-menu"
              @click.stop=""
            >
              <div class="menu-header">Select Channel</div>
              <div class="menu-divider"></div>
              <button
                v-for="channel in portalModeStore.channelList"
                :key="channel.value"
                @click="() => { handleChannelSelection(channel.value as MeridianPortalModeType); closeAllDropdowns(); }"
                class="channel-menu-item"
              >
                <i :class="channel.icon" class="menu-icon"></i>
                <span>{{ channel.name }}</span>
              </button>
            </div>
          </div>

          <!-- User Dropdown - Redesigned -->
          <div class="nav-item-dropdown ml-2">
            <button 
              @click="toggleUserDropdown" 
              class="user-dropdown-trigger"
              :class="{'active': isUserDropdownOpen}"
            >
              <div class="user-avatar">
                <img
                  v-if="auth.user?.avatar"
                  :src="auth.user.avatar"
                  :alt="auth.user?.firstName + ' ' + auth.user?.lastName || 'User avatar'"
                  @error="handleImageError"
                />
                <i v-else class="fas fa-user"></i>
              </div>
            </button>
            
            <!-- User Dropdown Menu -->
            <div
              v-show="isUserDropdownOpen"
              class="nav-dropdown-menu user-dropdown-menu"
              @click.stop=""
            >
              <!-- User Info Header -->
              <div class="user-info">
                <div class="user-avatar-large">
                  <img
                    v-if="auth.user?.avatar"
                    :src="auth.user.avatar"
                    :alt="auth.user?.firstName + ' ' + auth.user?.lastName || 'User avatar'"
                    @error="handleImageError"
                  />
                  <i v-else class="fas fa-user"></i>
                </div>
                <div class="user-details">
                  <div class="user-name text-[var(--text-color)]">{{ auth.user?.firstName }} {{ auth.user?.lastName }}</div>
                  <div class="user-email text-[var(--text-secondary)]">{{ auth.user?.email }}</div>
                </div>
              </div>

              <!-- Role Switcher Dropdown -->
              <div v-if="auth.user?.tpoPortalRole === PortalRoleType.PROCESSOR_MANAGER" class="role-switcher-container">
                <div class="role-switcher-header">
                  <span class="role-switcher-label">Role</span>
                </div>
                <div class="role-switcher-dropdown">
                  <button
                    @click.stop="toggleRoleDropdown"
                    :class="['role-switcher-trigger', { 'active': isRoleDropdownOpen }]"
                    :disabled="isSwitchingRole"
                  >
                    <i :class="getCurrentRoleIcon()" class="menu-icon"></i>
                    <span>{{ getCurrentRoleLabel() }}</span>
                    <i class="fas fa-chevron-down role-chevron" :class="{ 'rotate-180': isRoleDropdownOpen }"></i>
                  </button>
                  <div
                    v-show="isRoleDropdownOpen"
                    class="role-switcher-menu"
                    @click.stop=""
                  >
                    <button
                      @click="handleRoleSwitch(auth.user?._id || '', PortalRoleType.BROKER)"
                      :class="['role-switcher-option', { 'active': tpoPortalRoleStore.getCurrentRole() === PortalRoleType.BROKER}]"
                      :disabled="isSwitchingRole"
                    >
                      <i class="fas fa-user-tie menu-icon"></i>
                      <span>{{ PortalRoleType.BROKER }}</span>
                      <i v-if="tpoPortalRoleStore.getCurrentRole() === PortalRoleType.BROKER" class="fas fa-check role-check"></i>
                    </button>
                    <button
                      @click="handleRoleSwitch(auth.user?._id || '', PortalRoleType.PROCESSOR_MANAGER)"
                      :class="['role-switcher-option', { 'active': tpoPortalRoleStore.getCurrentRole() === PortalRoleType.PROCESSOR_MANAGER }]"
                      :disabled="isSwitchingRole"
                    >
                      <i class="fas fa-users-cog menu-icon"></i>
                      <span>{{ PortalRoleType.PROCESSOR_MANAGER }}</span>
                      <i v-if="tpoPortalRoleStore.getCurrentRole() === PortalRoleType.PROCESSOR_MANAGER" class="fas fa-check role-check"></i>
                    </button>
                  </div>
                </div>
              </div>
              
              <!-- Menu Items -->
              <div class="menu-divider"></div>
              <router-link to="/profile" class="menu-item" @click="closeAllDropdowns">
                <i class="fas fa-user-circle menu-icon"></i>
                <span>Profile</span>
              </router-link>
              
              <router-link to="/settings" class="menu-item" @click="closeAllDropdowns">
                <i class="fas fa-cog menu-icon"></i>
                <span>Settings</span>
              </router-link>
              
              <router-link to="/company" class="menu-item" @click="closeAllDropdowns">
                <i class="fas fa-building menu-icon"></i>
                <span>Company</span>
              </router-link>

              <router-link 
                v-if="isCorporate"
                to="/members" 
                class="menu-item"
                @click="closeAllDropdowns"
              >
                <i class="fas fa-users menu-icon"></i>
                <span>Member</span>
              </router-link>
              
              <div class="menu-divider"></div>
              <button @click="logout" class="menu-item logout-item">
                <i class="fas fa-sign-out-alt menu-icon"></i>
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </template>

        <router-link v-else to="/login" class="sign-in-btn" :data-theme="themeStore.isDark ? 'dark' : 'light'">Sign In</router-link>

        <!-- Theme Toggle -->
        <button @click="themeStore.toggleTheme" class="theme-toggle-btn">
          <i :class="themeStore.isDark ? 'fas fa-sun' : 'fas fa-moon'"></i>
        </button>
      </div>

      <!-- Mobile Navigation -->
      <div class="mobile-nav">
        <button @click="toggleMobileMenu" class="mobile-menu-btn">
          <i :class="isMobileMenuOpen ? 'fas fa-times' : 'fas fa-bars'"></i>
        </button>
      </div>
    </nav>

    <!-- Company Switch Loading Modal -->
    <CompanySwitchLoadingModal
      :is-open="isSwitchingCompany"
      @update:is-open="isSwitchingCompany = $event"
      title="Switching company"
      message="Please wait while we switch your company"
    />

    <main class="flex-1 overflow-y-auto bg-[var(--background-color)]" :class="{ 'nav-hidden': hideNav }">
      <router-view v-if="auth.isInitialized && !isLoading" v-slot="{ Component }">
        <template v-if="isLoginView">
          <component :is="Component" class="pt-2"/>
        </template>
        <transition v-else name="fade" mode="out-in">
          <component :is="Component" :class="hideNav ? '' : 'pt-7'"/>
        </transition>
      </router-view>
      <div v-else class="loading-container">
        <div class="loading-spinner"></div>
      </div>
    </main>

    <footer v-if="auth.isInitialized && !isLoading && !hideNav" class="footer">
      <div class="footer-content">
        <div class="footer-main">
          <div class="footer-col">
            <img
              :src="BRAND_LOGO_URL"
              :alt="BRAND_LOGO_ALT"
              class="footer-logo light-logo"
            />
            <img
              :src="BRAND_LOGO_DARK_URL"
              :alt="BRAND_LOGO_ALT"
              class="footer-logo dark-logo"
              aria-hidden="true"
            />
            <p class="address">
              <i class="fas fa-map-marker-alt"></i>
              Union Home Mortgage Corp., 8241 Dow Circle W, Strongsville, OH 44136
            </p>
          </div>
          <div class="footer-col">
            <h4>Contact Us</h4>
            <div class="contact-links">
              <a href="tel:4402344300">
                <i class="fas fa-phone"></i>
                440-234-4300
              </a>
              <a href="mailto:info@ameritrustTPO.com">
                <i class="fas fa-envelope"></i>
                info@ameritrustTPO.com
              </a>
            </div>
          </div>
          <div class="footer-col">
            <h4>Licensing</h4>
            <div class="license-items">
              <div class="license-item">
                <i class="fas fa-id-card"></i>
                <span>FHA #382708</span>
              </div>
              <div class="license-item">
                <i class="fas fa-shield-alt"></i>
                <span>VA #5650720000</span>
              </div>
              <div class="license-item">
                <i class="fas fa-certificate"></i>
                <span>NMLS #2229</span>
              </div>
            </div>
          </div>
        </div>
        <div class="footer-divider"></div>
        <div class="footer-legal">
          <div class="legal-left">
            <img
              src="https://ameritrusttpo.com/wp-content/uploads/Equal_Housing_Lender-logo-6C407AF4DE-seeklogo.com_.png"
              alt="Equal Housing Lender"
              class="equal-housing-logo"
            />
            <a
              href="https://www.nmlsconsumeraccess.org/"
              target="_blank"
              rel="noopener noreferrer"
              class="nmls-link"
            >
              NMLS Consumer Access
            </a>
          </div>
          <p class="copyright">© {{ currentYear }} AMERITRUST MORTGAGE, A UHM COMPANY. ALL RIGHTS RESERVED</p>
        </div>
      </div>
    </footer>
  </div>
  <div v-else class="loading-container">
    <div class="loading-spinner"></div>
  </div>

  <!-- Mobile Menu Overlay -->
  <div 
    v-if="isMobileMenuOpen" 
    class="mobile-menu-overlay"
    @click="closeMobileNav"
  ></div>
  
  <!-- Mobile Menu Panel -->
  <div 
    v-show="isMobileMenuOpen"
    class="mobile-menu-panel"
    :class="{'open': isMobileMenuOpen}"
  >
    <template v-if="auth.isAuthenticated">
      <router-link to="/" class="mobile-logo">
        <img
          :src="BRAND_LOGO_URL"
          :alt="BRAND_LOGO_ALT"
          class="mobile-light-logo"
        />
        <img 
          :src="BRAND_LOGO_DARK_URL" 
          :alt="BRAND_LOGO_ALT" 
          class="mobile-dark-logo" 
        />
      </router-link>
      <div class="mobile-user-info">
        <div class="mobile-user-avatar">
          <img
            v-if="auth.user?.avatar"
            :src="auth.user.avatar"
            :alt="auth.user?.firstName"
            @error="handleImageError"
          />
          <i v-else class="fas fa-user"></i>
        </div>
        <div class="mobile-user-details">
          <div class="mobile-user-name">{{ auth.user?.firstName }} {{ auth.user?.lastName }}</div>
          <div class="mobile-user-email">{{ auth.user?.email }}</div>
        </div>
      </div>

      <router-link 
        to="/union-home-mortgage-loans" 
        class="mobile-menu-item"
        @click="closeMobileNav"
      >Union Home Mortgage Loans</router-link>

      <router-link 
        to="/dashboard" 
        class="mobile-menu-item"
        @click="closeMobileNav"
      >Dashboard</router-link>
      
      <router-link 
        to="/loans" 
        class="mobile-menu-item"
        @click="closeMobileNav"
        v-if="tpoPortalRoleStore.getCurrentRole() === PortalRoleType.BROKER && !multipleCompanyAccessStore.getHasMultipleCompanyAccess()"
      >Pipeline</router-link>

      <router-link 
        to="/multiple-company-access-pipeline" 
        class="mobile-menu-item"
        @click="closeMobileNav"
        v-if="tpoPortalRoleStore.getCurrentRole() === PortalRoleType.BROKER && multipleCompanyAccessStore.getHasMultipleCompanyAccess()"
      >Pipeline</router-link>
      
      <router-link 
        v-if="tpoPortalRoleStore.getCurrentRole() === PortalRoleType.PROCESSOR_MANAGER"
        to="/team-loans" 
        class="mobile-menu-item"
        @click="closeMobileNav"
      >Team Pipeline</router-link>
      
      <!-- <router-link 
        to="/loan-applications" 
        class="mobile-menu-item"
        @click="closeMobileNav"
      >Apps</router-link> -->
      
      <!-- Mobile Company Dropdown -->
      <div v-if="isCorporate" class="mobile-dropdown">
        <button 
          @click.stop="toggleMobileCompanyDropdown" 
          class="mobile-dropdown-trigger"
        >
          <span>Company</span>
          <i class="fas fa-chevron-down" 
            :class="{'rotate-180': isMobileCompanyOpen}"
          ></i>
        </button>
        
        <div 
          v-show="isMobileCompanyOpen"
          class="mobile-dropdown-content"
        >
          <router-link 
            to="/members" 
            class="mobile-menu-subitem"
            @click="closeMobileNav"
          >
            <i class="fas fa-users menu-icon"></i>
            <span>Member</span>
          </router-link>
        </div>
      </div>
      
      <!-- Mobile New Loan Dropdown -->
      <div class="mobile-dropdown">
        <button 
          @click.stop="toggleMobileNewLoanDropdown" 
          class="mobile-dropdown-trigger"
          :class="{'active': isNewLoanActive}"
        >
          <span>New Loan</span>
          <i class="fas fa-chevron-down" 
            :class="{'rotate-180': isMobileNewLoanOpen}"
          ></i>
        </button>
        
        <div 
          v-show="isMobileNewLoanOpen"
          class="mobile-dropdown-content"
        >
          <router-link 
            to="/loan-application/create" 
            class="mobile-menu-subitem"
            @click="closeMobileNav"
            :class="{'active': isNewLoanActive}"
          >
            <i class="fas fa-plus-circle menu-icon"></i>
            <span>Create From Scratch</span>
          </router-link>
          <router-link
            v-if="showMismoUploadClassic"
            to="/loan-application/upload"
            class="mobile-menu-subitem"
            @click="closeMobileNav"
            :class="{'active': isNewLoanActive}"
          >
            <i class="fas fa-upload menu-icon"></i>
            <span>Upload MISMO file</span>
          </router-link>
          <router-link
            v-if="showMismoUploadPrepare"
            to="/loan-application/upload-prepare"
            class="mobile-menu-subitem"
            @click="closeMobileNav"
            :class="{'active': isNewLoanActive}"
          >
            <i class="fas fa-upload menu-icon"></i>
            <span>Upload MISMO file</span>
          </router-link>
          <router-link 
            to="/resources/guide" 
            class="mobile-menu-subitem"
            @click="closeMobileNav"
          >
            <i class="fas fa-book-open menu-icon"></i>
            <span>Guide - Create a Loan</span>
          </router-link>
        </div>
      </div>
      
      <!-- Mobile Pricing Dropdown -->
      <div class="mobile-dropdown">
        <button
          @click.stop="toggleMobilePricingDropdown"
          class="mobile-dropdown-trigger"
          :class="{'active': isPricingActive}"
        >
          <span>Pricing</span>
          <i class="fas fa-chevron-down"
            :class="{'rotate-180': isMobilePricingOpen}"
          ></i>
        </button>

        <div
          v-show="isMobilePricingOpen"
          class="mobile-dropdown-content"
        >
          <router-link
            to="/quick-pricer-menu"
            class="mobile-menu-subitem"
            @click="closeMobileNav"
            :class="{'active': isPricingActive}"
          >
            <i class="fas fa-calculator menu-icon"></i>
            <span>Quick Pricer</span>
          </router-link>
          <router-link
            to="/scenario-desk"
            class="mobile-menu-subitem"
            @click="closeMobileNav"
            :class="{'active': isPricingActive}"
          >
            <i class="fas fa-chart-line menu-icon"></i>
            <span>Pricing Scenario</span>
          </router-link>
          <div class="mobile-menu-subitem coming-soon-nav" style="opacity:0.5; pointer-events:none;">
            <i class="fas fa-dollar-sign menu-icon"></i>
            <span>Price A Loan <em>(Coming soon)</em></span>
          </div>
        </div>
      </div>
      
      <!-- Mobile Tools Dropdown -->
      <div class="mobile-dropdown">
        <button 
          @click.stop="toggleMobileToolsDropdown" 
          class="mobile-dropdown-trigger"
          :class="{'active': isToolsActive}"
        >
          <span>Tools</span>
          <i class="fas fa-chevron-down" 
            :class="{'rotate-180': isMobileToolsOpen}"
          ></i>
        </button>
        
        <div 
          v-show="isMobileToolsOpen"
          class="mobile-dropdown-content"
        >
          <router-link 
            to="/tools/how-to-videos" 
            class="mobile-menu-subitem"
            @click="closeMobileNav"
            :class="{'active': isToolsActive}"
          >
            <i class="fas fa-video menu-icon"></i>
            <span>How-to Videos</span>
          </router-link>
        </div>
      </div>
      
      <!-- Mobile Resources Dropdown -->
      <div class="mobile-dropdown">
        <button 
          @click.stop="toggleMobileResourcesDropdown" 
          class="mobile-dropdown-trigger"
          :class="{'active': isResourcesActive}"
        >
          <span>Resources</span>
          <i class="fas fa-chevron-down" 
            :class="{'rotate-180': isMobileResourcesOpen}"
          ></i>
        </button>
        
        <div 
          v-show="isMobileResourcesOpen"
          class="mobile-dropdown-content"
        >
          <router-link 
            to="/resources" 
            class="mobile-menu-subitem"
            @click="closeMobileNav"
            :class="{'active': isResourcesActive}"
          >
            <i class="fas fa-home menu-icon"></i>
            <span>Resources Home</span>
          </router-link>
          <router-link 
            to="/resources/exclusionary-list" 
            class="mobile-menu-subitem"
            @click="closeMobileNav"
            :class="{'active': isResourcesActive}"
          >
            <i class="fas fa-ban menu-icon"></i>
            <span>Exclusionary List</span>
          </router-link>
          <router-link 
            to="/resources/appraisal" 
            class="mobile-menu-subitem"
            @click="closeMobileNav"
            :class="{'active': isResourcesActive}"
          >
            <i class="fas fa-chart-line menu-icon"></i>
            <span>Appraisal</span>
          </router-link>
          <router-link 
            to="/resources/programs" 
            class="mobile-menu-subitem"
            @click="closeMobileNav"
            :class="{'active': isResourcesActive}"
          >
            <i class="fas fa-list-alt menu-icon"></i>
            <span>Programs</span>
          </router-link>
          <router-link 
            to="/resources/forms" 
            class="mobile-menu-subitem"
            @click="closeMobileNav"
            :class="{'active': isResourcesActive}"
          >
            <i class="fas fa-file-alt menu-icon"></i>
            <span>Forms</span>
          </router-link>
          <div class="mobile-menu-subitem coming-soon-nav" style="opacity:0.5; pointer-events:none;">
            <i class="fas fa-university menu-icon"></i>
            <span>
              Bank Statement Submission
              <span class="coming-soon-badge">Coming Soon</span>
            </span>
          </div>
          <div class="mobile-menu-subitem coming-soon-nav" style="opacity:0.5; pointer-events:none;">
            <i class="fas fa-calculator menu-icon"></i>
            <div>
              <span>Calculators</span>
              <span class="coming-soon-badge">Coming Soon</span>
            </div>
          </div>
          <router-link 
            to="/resources/mortgage-clause" 
            class="mobile-menu-subitem"
            @click="closeMobileNav"
            :class="{'active': isResourcesActive}"
          >
            <i class="fas fa-file-contract menu-icon"></i>
            <span>Mortgagee Clause</span>
          </router-link>
          <router-link 
            to="/resources/wholesale-turntimes" 
            class="mobile-menu-subitem"
            @click="closeMobileNav"
            :class="{'active': isResourcesActive}"
          >
            <i class="fas fa-clock menu-icon"></i>
            <span>Wholesale Turn Times</span>
          </router-link>
          <router-link 
            to="/resources/licensing" 
            class="mobile-menu-subitem"
            @click="closeMobileNav"
            :class="{'active': isResourcesActive}"
          >
            <i class="fas fa-id-card menu-icon"></i>
            <span>Licensing</span>
          </router-link>
          <router-link 
            to="/resources/guide" 
            class="mobile-menu-subitem"
            @click="closeMobileNav"
            :class="{'active': isResourcesActive}"
          >
            <i class="fas fa-book-open menu-icon"></i>
            <span>Guide</span>
          </router-link>
        </div>
      </div>
      
      <!-- Mobile TPOP Agent -->
      <router-link 
        :to="tpopAgentLink" 
        class="mobile-menu-item"
        @click="closeMobileNav"
        :class="{'active': isTpopAgentActive}"
      >
        <span>TPOP Agent</span>
      </router-link>
      
      <!-- Mobile Portal Mode Dropdown -->
      <div v-if="!portalModeStore.isFetchingPortalMode && portalModeStore.hasMultiplePortalModes" class="mobile-dropdown">
        <button 
          @click.stop="toggleMobilePortalModeDropdown" 
          class="mobile-dropdown-trigger portal-mode-trigger"
          :class="{'active': isMobilePortalModeOpen || isPortalModeActive}"
        >

          <span>{{ MeridianPortalModeTypeToPortalModeType(portalModeStore.currentSelection as MeridianPortalModeType) }}</span>
          <i class="fas fa-chevron-down" 
            :class="{'rotate-180': isMobilePortalModeOpen}"
          ></i>
        </button>
        
        <div 
          v-show="isMobilePortalModeOpen"
          class="mobile-dropdown-content"
        >
          <button
            v-for="channel in portalModeStore.channelList"
            :key="channel.value"
            @click="() => { handleChannelSelection(channel.value as MeridianPortalModeType); closeMobileNav(); }"
            class="mobile-menu-subitem"
          >
            <i :class="channel.icon" class="menu-icon"></i>
            <span>{{ channel.name }}</span>
          </button>
        </div>
      </div>
      
      <div class="mobile-menu-divider"></div>
      
      <!-- User Section -->
      <div class="mobile-user-section">  
        <router-link 
          to="/profile" 
          class="mobile-menu-item"
          @click="closeMobileNav"
        >
          <i class="fas fa-user-circle menu-icon"></i>
          <span>Profile</span>
        </router-link>
        
        <router-link 
          to="/settings" 
          class="mobile-menu-item"
          @click="closeMobileNav"
        >
          <i class="fas fa-cog menu-icon"></i>
          <span>Settings</span>
        </router-link>
        
        <router-link 
          to="/company" 
          class="mobile-menu-item"
          @click="closeMobileNav"
        >
          <i class="fas fa-building menu-icon"></i>
          <span>Company</span>
        </router-link>
        
        <router-link 
          v-if="isCorporate"
          to="/members" 
          class="mobile-menu-item"
          @click="closeMobileNav"
        >
          <i class="fas fa-users menu-icon"></i>
          <span>Member</span>
        </router-link>
        
        <button 
          @click="logout" 
          class="mobile-menu-item mobile-logout-btn"
        >
          <i class="fas fa-sign-out-alt menu-icon"></i>
          <span>Sign Out</span>
        </button>
      </div>
    </template>
    
    <template v-else>
      <router-link 
        to="/login" 
        class="mobile-menu-item mobile-sign-in"
        @click="closeMobileNav"
      >Sign In</router-link>
    </template>
    
    <!-- Mobile Theme Toggle -->
    <button 
      @click="themeStore.toggleTheme" 
      class="mobile-theme-toggle"
    >
      <i :class="themeStore.isDark ? 'fas fa-sun' : 'fas fa-moon'"></i>
      <span>{{ themeStore.isDark ? 'Light Mode' : 'Dark Mode' }}</span>
    </button>
  </div>

  <!-- Global notification snackbar -->
  <Transition name="fade">
    <div v-if="snackbarStore.isVisible" class="snackbar" :class="snackbarStore.type">
      <div class="snackbar-icon">
        <i v-if="snackbarStore.type === 'success'" class="fas fa-check-circle"></i>
        <i v-if="snackbarStore.type === 'error'" class="fas fa-exclamation-circle"></i>
        <i v-if="snackbarStore.type === 'warning'" class="fas fa-exclamation-triangle"></i>
        <i v-if="snackbarStore.type === 'info'" class="fas fa-info-circle"></i>
      </div>
      {{ snackbarStore.message }}
      <button @click="snackbarStore.hide()" class="close-btn">
        <i class="fas fa-times"></i>
      </button>
    </div>
  </Transition>

  <!-- Channel Switcher Loader -->
  <ChannelSwitcherLoader :is-visible="isSwitchingPortalMode" :channel-name="switchingToChannel" />
</template>

<script setup lang="ts">
  import { onBeforeMount, ref, watch, onMounted, computed, onUnmounted } from 'vue'
  import { useSnackbarStore } from '@/stores/snackbar'
  import { useAuthStore } from '@/stores/auth'
  import { useThemeStore } from '@/stores/theme'
  import { useRouter } from 'vue-router'
  import DynamicDialogs from '@/components/ui/modal/DynamicDialogs.vue'
  import { useDialog } from '@/composables/useDialog'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import { MeridianPortalModeType } from '@/enums/meridian/meridianPortalModeType'
  import { MeridianPortalModeTypeToPortalModeType } from '@/types/typeConversion/TypeConversion'
  import loanService from '@/services/loan.service'
  import ChannelSwitcherLoader from '@/components/ChannelSwitcherLoader.vue'
  import { usePortalModeStore } from '@/stores/portalModeStore'
  import { useFundedLoanAccessStore } from '@/stores/fundedLoanAccessStore'
  import { PortalRoleType } from '@/enums/portal/portalRoleType'
  import userService from '@/services/user.service'
  import { useTpoPortalRoleStore } from '@/stores/tpoPortalRoleStore'
  import companiesService from '@/services/companies.service'
  import type { Company } from '@/services/companies.service'
  import CompanySwitchLoadingModal from '@/components/ui/modal/CompanySwitchLoadingModal.vue'
  import { useMultipleCompanyAccessStore } from '@/stores/multipleCompanyAccessStore'
  import authService from '@/services/auth.service'
  import { userCompanyService } from '@/services/usercompany.service'
  import { BRAND_LOGO_ALT, BRAND_LOGO_DARK_URL, BRAND_LOGO_URL } from '@/constants/brand'
  import moment from 'moment-timezone'

  // Declare global property for JWT login loading
  declare global {
    interface Window {
      jwtLoginLoading?: boolean
    }
  }

  // Store initialization
  const snackbarStore = useSnackbarStore()
  const portalModeStore = usePortalModeStore()
  const fundedLoanAccessStore = useFundedLoanAccessStore()
  const tpoPortalRoleStore = useTpoPortalRoleStore()
  const multipleCompanyAccessStore = useMultipleCompanyAccessStore()
  const auth = useAuthStore()
  const themeStore = useThemeStore()
  const router = useRouter()
  const dialog = useDialog()
  const {
    init: initFeatureFlags,
    getFlag,
    hasFeature,
    getValue,
    onChange,
    isFeatureEnabledLikeCrisp,
    refresh: refreshFeatureFlags,
  } = useFeatureFlag()
  const user = computed(() => JSON.parse(localStorage.getItem('user') || '{}'))

  const FLAG_LOAN_MISMO_UPLOAD_CLASSIC = 'loan_mismo_upload_classic'
  const FLAG_LOAN_MISMO_UPLOAD_PREPARE = 'loan_mismo_upload_prepare'
  const FLAG_LOAN_MISMO_UPLOAD_PREPARE_USER = 'loan_mismo_upload_prepare_user'

  /**
   * `loan_mismo_upload_prepare` (global) + `loan_mismo_upload_prepare_user` (server-evaluated email list):
   * - Global OFF: show upload-prepare only when `loan_mismo_upload_prepare_user` is enabled for this identity (allowlist).
   * - Global ON: show upload-prepare for everyone except when that identity flag is on (list acts as blocklist).
   *
   * The API never sends raw email arrays; it sets `enabled`/`value` via sanitizeLoanMismoPrepareUserFlag — use `hasFeature`, not `getValue === true` alone.
   */
  const showMismoUploadPrepare = computed(() => {
    const globalPrepare = isFeatureEnabledLikeCrisp(FLAG_LOAN_MISMO_UPLOAD_PREPARE)
    const userMatchesPrepareList = hasFeature(FLAG_LOAN_MISMO_UPLOAD_PREPARE_USER)
    return globalPrepare ? !userMatchesPrepareList : userMatchesPrepareList
  })

  /** Classic and prepare are mutually exclusive; prepare wins when `showMismoUploadPrepare` is true. */
  const showMismoUploadClassic = computed(() => {
    if (showMismoUploadPrepare.value) {
      return false
    }
    const f = getFlag(FLAG_LOAN_MISMO_UPLOAD_CLASSIC)
    if (f == null) return true
    return isFeatureEnabledLikeCrisp(FLAG_LOAN_MISMO_UPLOAD_CLASSIC)
  })

  /** Identity flags (e.g. prepare-user allowlist) require GET /flagsmith with a user; re-fetch after login/logout/switch. */
  watch(
    () => auth.user?._id,
    async (userId, previousUserId) => {
      if (userId === previousUserId) return
      try {
        await refreshFeatureFlags()
      } catch {
        /* ignore */
      }
    }
  )

  // Get current year in Pacific timezone (Los Angeles)
  const currentYear = computed(() => moment().tz('America/Los_Angeles').year())

  const crispInitialized = ref(false)
  const crispEnableAllEnabled = ref(false)
  const showSystemMaintenance = ref(false)
  const lastCrispUserId = ref<string | null>(null)
  let crispHoursIntervalId: ReturnType<typeof setInterval> | null = null

  type CrispCommand = [string, ...any[]]
  interface CrispUserIdentity {
    email?: string | null
    nickname?: string | null
  }

  const pushCrispCommand = (command: CrispCommand) => {
    try {
      const w = window as any
      if (!w.$crisp) {
        w.$crisp = []
      }
      if (typeof w.$crisp.push === 'function') {
        w.$crisp.push(command)
      }
    } catch (_) {}
  }

  const buildCrispIdentity = (): CrispUserIdentity => {
    const email = auth.user?.email || null
    const nickname =
      [auth.user?.firstName, auth.user?.lastName].filter(Boolean).join(' ').trim() ||
      (auth.user as any)?.name ||
      null
    return {
      email,
      nickname: nickname || null
    }
  }

  const isCrispWithinSupportHours = () => {
    const easternHour = moment().tz('America/New_York').hour()
    return easternHour >= 8 && easternHour < 20
  }

  const evaluateCrispGate = () => {
    try {
      if (!auth.isAuthenticated || !auth.user) {
        pushCrispCommand(['do', 'chat:hide'])
        return
      }

      const currentUserId = (auth.user?._id || '') as string

      if (lastCrispUserId.value && currentUserId && lastCrispUserId.value !== currentUserId) {
        pushCrispCommand(['do', 'session:reset'])
      }

      const crispEnableAllValue = getValue('crisp_enable_all')
      const crispUserValue = getValue('crisp_user')

      const isCrispEnableAll = crispEnableAllEnabled.value && (
        crispEnableAllValue === true ||
        crispEnableAllValue === 'true' ||
        crispEnableAllValue === 1
      )

      const hasCrispUserAccess = crispUserValue === true ||
        crispUserValue === 'true' ||
        crispUserValue === 1

      if ((isCrispEnableAll || hasCrispUserAccess) && isCrispWithinSupportHours()) {
        initCrisp(buildCrispIdentity())
        lastCrispUserId.value = currentUserId || null
        return
      }

      pushCrispCommand(['set', 'user:email', null])
      pushCrispCommand(['set', 'user:nickname', null])
      pushCrispCommand(['do', 'chat:hide'])
      lastCrispUserId.value = null
    } catch (_) { /* ignore */ }
  }
  const initCrisp = (identity?: CrispUserIdentity) => {
    if (crispInitialized.value) {
      // Update email if provided after initialization
      try {
        if (identity) {
          if (identity.email !== undefined) {
            pushCrispCommand(['set','user:email', identity.email])
          }
          if (identity.nickname !== undefined) {
            pushCrispCommand(['set','user:nickname', identity.nickname])
          }
        }
        setTimeout(() => {
          if (isCrispWithinSupportHours()) {
            pushCrispCommand(['do','chat:show'])
          } else {
            pushCrispCommand(['do', 'chat:hide'])
          }
        }, 100)
      } catch (_) { /* ignore */ }
      return
    }
    try {
      ;(function(){
        const w = window as any
        w.$crisp = w.$crisp || []
        w.CRISP_WEBSITE_ID = '6d4faa61-6a55-4898-b930-312481c5f052'
        const d = document
        const s = d.createElement('script')
        s.src = 'https://client.crisp.chat/l.js'
        s.async = true
        s.onload = () => {
          try {
            if (identity) {
              if (identity.email !== undefined) {
                pushCrispCommand(['set','user:email', identity.email])
              }
              if (identity.nickname !== undefined) {
                pushCrispCommand(['set','user:nickname', identity.nickname])
              }
            }
            setTimeout(() => {
              if (isCrispWithinSupportHours()) {
                pushCrispCommand(['do','chat:show'])
              } else {
                pushCrispCommand(['do', 'chat:hide'])
              }
            }, 100)
          } catch (_) { /* ignore */ }
        }
        d.getElementsByTagName('head')[0].appendChild(s)
      })()
      crispInitialized.value = true
    } catch (_) { /* ignore */ }
  }

  // Track states
  const isReady = ref(false)
  const isLoading = ref(true)
  const isLoginView = computed(() => {
    const path = router.currentRoute.value.path
    return path === '/login' ||
           path === '/forgot-password' ||
           path === '/register'
  })

  const hideNav = computed(() => !!router.currentRoute.value.meta?.hideNav)

  // Update body class based on route
  watch(() => router.currentRoute.value.path, (path) => {
    if (path === '/login') {
      document.body.classList.add('login-page')
      document.body.classList.remove('forgot-password-page', 'register-page')
    } else if (path === '/forgot-password') {
      document.body.classList.add('forgot-password-page')
      document.body.classList.remove('login-page', 'register-page')
    } else if (path === '/register') {
      document.body.classList.add('register-page')
      document.body.classList.remove('login-page', 'forgot-password-page')
    } else {
      document.body.classList.remove('login-page', 'forgot-password-page', 'register-page')
    }
  }, { immediate: true })

  // Dropdown states
  const isCompanyDropdownOpen = ref(false)
  const isCompanySwitchDropdownOpen = ref(false)
  const isUserDropdownOpen = ref(false)
  const isMobileMenuOpen = ref(false)
  const isMobileCompanyOpen = ref(false)
  const isNewLoanDropdownOpen = ref(false)
  const isMobileNewLoanOpen = ref(false)
  const isResourcesDropdownOpen = ref(false)
  const isPortalModeDropdownOpen = ref(false)
  const isPortalModeActive = ref(false)
  const isSwitchingPortalMode = ref(false)
  const switchingToChannel = ref('')
  const isMobileResourcesOpen = ref(false)
  const isMobilePortalModeOpen = ref(false)
  const isPricingDropdownOpen = ref(false)
  const isMobilePricingOpen = ref(false)
  const isToolsDropdownOpen = ref(false)
  const isMobileToolsOpen = ref(false)
  const isCorporate = ref(false)
  const isSwitchingRole = ref(false)
  const isRoleDropdownOpen = ref(false)
  
  // Company switching
  const companies = ref<Company[]>([])
  const isLoadingCompanies = ref(false)
  const isSwitchingCompany = ref(false)
  const currentCompanyId = computed(() => {
    try {
      const company = localStorage.getItem('company')
      return company ? JSON.parse(company).id : null
    } catch {
      return null
    }
  })

  const channelList = ref<{ name: string; value: string; icon: string }[]>([])

  const companyRoutes = ['/members'];
  const pricingRoutes = ['/quick-pricer-menu', '/scenario-desk'];
  const toolsRoutes = ['/tools/how-to-videos'];
  const newLoanRoutes = [
    '/loan-application/new',
    '/loan-application/create',
    '/loan-application/upload',
    '/loan-application/upload-prepare',
  ]
  const resourcesRoutes = ['/resources', '/resources/exclusionary-list', '/resources/appraisal', '/resources/programs', '/resources/forms', '/resources/bank-statement-submission', '/resources/calculators', '/resources/mortgage-clause', '/resources/wholesale-turntimes', '/resources/licensing', '/resources/guide'];

  const isCompanyActive = computed(() => companyRoutes.includes(router.currentRoute.value.path));
  const isPricingActive = computed(() => pricingRoutes.includes(router.currentRoute.value.path));
  const isNewLoanActive = computed(() => newLoanRoutes.includes(router.currentRoute.value.path));
  const isToolsActive = computed(() => toolsRoutes.includes(router.currentRoute.value.path));
  const isResourcesActive = computed(() => resourcesRoutes.includes(router.currentRoute.value.path));
  const isTpopAgentActive = computed(() => router.currentRoute.value.path === '/tpop-agent');
  const tpopAgentLink = computed(() => {
    const route = router.currentRoute.value
    if (route.name === 'loan-detail' && route.params.id) {
      return { path: '/tpop-agent', query: { loanId: route.params.id } }
    }
    return '/tpop-agent'
  });

  // Methods
  const handleLogout = async () => {
    isLoading.value = true
    try {
      try {
        await authService.logout()
      } catch (apiError) {
        console.error('Logout API error:', apiError)
      }
      auth.logoutOnly()

      localStorage.clear()
      sessionStorage.clear()

      window.location.href = '/login'
    } catch (error) {
      console.error('Logout error:', error)
      auth.logoutOnly()
      localStorage.clear()
      sessionStorage.clear()
      window.location.href = '/login'
    } finally {
      isLoading.value = false
    }
  }

  // Toggle dropdowns
  const toggleCompanyDropdown = (event: Event) => {
    event.stopPropagation()
    isUserDropdownOpen.value = false
    isNewLoanDropdownOpen.value = false
    isResourcesDropdownOpen.value = false
    isPortalModeDropdownOpen.value = false
    isPricingDropdownOpen.value = false
    isToolsDropdownOpen.value = false
    isCompanyDropdownOpen.value = !isCompanyDropdownOpen.value
  }

  const toggleCompanySwitchDropdown = (event: Event) => {
    event.stopPropagation()
    const wasOpen = isCompanySwitchDropdownOpen.value
    isCompanySwitchDropdownOpen.value = !isCompanySwitchDropdownOpen.value
    
    // Always refetch companies when opening the dropdown
    if (isCompanySwitchDropdownOpen.value && !wasOpen) {
      fetchCompanies()
    }
  }

  const fetchCompanies = async () => {
    if (isLoadingCompanies.value) return
    try {
      isLoadingCompanies.value = true
      const response = await companiesService.getAssignedCompanies()
      if (response.success && response.data) {
        companies.value = response.data.companies || []
        
        // Set current company from selectedCompanyId if available
        if (response.data.selectedCompanyId) {
          const selectedCompany = companies.value.find((c: Company) => c.id === String(response.data.selectedCompanyId))
          if (selectedCompany) {
            // Update localStorage with selected company
            localStorage.setItem('company', JSON.stringify(selectedCompany))
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch assigned companies:', error)
    } finally {
      isLoadingCompanies.value = false
    }
  }

  const handleCompanySwitch = async (company: Company) => {
    if (currentCompanyId.value === String(company.id) || company.isSelected) {
      isCompanySwitchDropdownOpen.value = false
      return
    }

    try {
      isSwitchingCompany.value = true
      isCompanySwitchDropdownOpen.value = false
      closeAllDropdowns()
      
      // Call backend API to update is_selected_company in mortdash and MongoDB companyId
      await companiesService.switchSelectedCompany(company.id)
      
      // Update localStorage - company object
      localStorage.setItem('company', JSON.stringify(company))
      
      // Update localStorage - user object with new companyId
      const userStr = localStorage.getItem('user')
      if (userStr) {
        try {
          const user = JSON.parse(userStr)
          user.companyId = String(company.id)
          localStorage.setItem('user', JSON.stringify(user))
          
          // Also update the auth store if it's available
          if (auth.user) {
            auth.setUser({ ...auth.user, companyId: String(company.id) })
          }
        } catch (e) {
          console.error('Error updating user in localStorage:', e)
        }
      }
      
      // Reload page to reflect new company context
      window.location.href = '/multiple-company-access-pipeline'
    } catch (error: any) {
      console.error('Error switching company:', error)
      isSwitchingCompany.value = false
      dialog.error('Error switching company', error.response?.data?.message || error.message || 'An error occurred while switching your company')
    }
  }

  const toggleUserDropdown = (event: Event) => {
    event.stopPropagation()
    isCompanyDropdownOpen.value = false
    isNewLoanDropdownOpen.value = false
    isResourcesDropdownOpen.value = false
    isPortalModeDropdownOpen.value = false
    isPricingDropdownOpen.value = false
    isToolsDropdownOpen.value = false
    isUserDropdownOpen.value = !isUserDropdownOpen.value
    if (!isUserDropdownOpen.value) {
      isRoleDropdownOpen.value = false
    }
  }

  const toggleRoleDropdown = (event: Event) => {
    event.stopPropagation()
    isRoleDropdownOpen.value = !isRoleDropdownOpen.value
  }

  const toggleMobileCompanyDropdown = (event: Event) => {
    event.stopPropagation()
    isMobileNewLoanOpen.value = false
    isMobileResourcesOpen.value = false
    isMobileToolsOpen.value = false
    isMobilePortalModeOpen.value = false
    isMobileCompanyOpen.value = !isMobileCompanyOpen.value
  }

  const toggleNewLoanDropdown = (event: Event) => {
    event.stopPropagation()
    isUserDropdownOpen.value = false
    isCompanyDropdownOpen.value = false
    isResourcesDropdownOpen.value = false
    isPortalModeDropdownOpen.value = false
    isPricingDropdownOpen.value = false
    isToolsDropdownOpen.value = false
    isNewLoanDropdownOpen.value = !isNewLoanDropdownOpen.value
  }

  const toggleMobileNewLoanDropdown = (event: Event) => {
    event.stopPropagation()
    isMobileCompanyOpen.value = false
    isMobileResourcesOpen.value = false
    isMobileToolsOpen.value = false
    isMobilePortalModeOpen.value = false
    isMobilePricingOpen.value = false
    isMobileNewLoanOpen.value = !isMobileNewLoanOpen.value
  }

  const toggleMobilePricingDropdown = (event: Event) => {
    event.stopPropagation()
    isMobileCompanyOpen.value = false
    isMobileNewLoanOpen.value = false
    isMobileResourcesOpen.value = false
    isMobileToolsOpen.value = false
    isMobilePortalModeOpen.value = false
    isResourcesDropdownOpen.value = false
    isMobilePricingOpen.value = !isMobilePricingOpen.value
  }

  const togglePricingDropdown = (event: Event) => {
    event.stopPropagation()
    isUserDropdownOpen.value = false
    isCompanyDropdownOpen.value = false
    isNewLoanDropdownOpen.value = false
    isResourcesDropdownOpen.value = false
    isPortalModeDropdownOpen.value = false
    isToolsDropdownOpen.value = false
    isPricingDropdownOpen.value = !isPricingDropdownOpen.value
  }

  const toggleToolsDropdown = (event: Event) => {
    event.stopPropagation()
    isUserDropdownOpen.value = false
    isCompanyDropdownOpen.value = false
    isNewLoanDropdownOpen.value = false
    isResourcesDropdownOpen.value = false
    isPortalModeDropdownOpen.value = false
    isPricingDropdownOpen.value = false
    isPortalModeDropdownOpen.value = false
    isToolsDropdownOpen.value = !isToolsDropdownOpen.value
  }

  const toggleMobileToolsDropdown = (event: Event) => {
    event.stopPropagation()
    isMobileCompanyOpen.value = false
    isMobileNewLoanOpen.value = false
    isMobileResourcesOpen.value = false
    isMobilePortalModeOpen.value = false
    isMobilePricingOpen.value = false
    isMobileToolsOpen.value = !isMobileToolsOpen.value
  }

  const toggleResourcesDropdown = (event: Event) => {
    event.stopPropagation()
    isUserDropdownOpen.value = false
    isCompanyDropdownOpen.value = false
    isNewLoanDropdownOpen.value = false
    isToolsDropdownOpen.value = false
    isPortalModeDropdownOpen.value = false
    isPricingDropdownOpen.value = false
    isResourcesDropdownOpen.value = !isResourcesDropdownOpen.value
  }

  const togglePortalModeDropdown = (event: Event) => {
    event.stopPropagation()
    isUserDropdownOpen.value = false
    isCompanyDropdownOpen.value = false
    isNewLoanDropdownOpen.value = false
    isPortalModeActive.value = false
    isToolsDropdownOpen.value = false
    isPricingDropdownOpen.value = false
    isResourcesDropdownOpen.value = false
    isPortalModeDropdownOpen.value = !isPortalModeDropdownOpen.value
  }

  const toggleMobileResourcesDropdown = (event: Event) => {
    event.stopPropagation()
    isMobileCompanyOpen.value = false
    isMobileNewLoanOpen.value = false
    isMobileToolsOpen.value = false
    isMobilePortalModeOpen.value = false
    isMobilePricingOpen.value = false
    isMobileResourcesOpen.value = !isMobileResourcesOpen.value
  }

  const toggleMobilePortalModeDropdown = (event: Event) => {
    event.stopPropagation()
    isMobileCompanyOpen.value = false
    isMobileNewLoanOpen.value = false
    isMobileResourcesOpen.value = false
    isMobileToolsOpen.value = false
    isMobilePricingOpen.value = false
    isMobilePortalModeOpen.value = !isMobilePortalModeOpen.value
  }

  // Close dropdowns
  const closeAllDropdowns = () => {
    isCompanyDropdownOpen.value = false
    isCompanySwitchDropdownOpen.value = false
    isUserDropdownOpen.value = false
    isMobileCompanyOpen.value = false
    isNewLoanDropdownOpen.value = false
    isMobileNewLoanOpen.value = false
    isPricingDropdownOpen.value = false
    isMobilePricingOpen.value = false
    isToolsDropdownOpen.value = false
    isMobileToolsOpen.value = false
    isResourcesDropdownOpen.value = false
    isMobileResourcesOpen.value = false
    isMobilePortalModeOpen.value = false
    isPortalModeDropdownOpen.value = false
    isRoleDropdownOpen.value = false
  }

  const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value
    if (!isMobileMenuOpen.value) {
      closeAllDropdowns()
    }
  }

  const closeMobileNav = () => {
    isMobileMenuOpen.value = false
    closeAllDropdowns()
  }

  const logout = () => {
    isMobileMenuOpen.value = false
    closeAllDropdowns()
    // Small delay to ensure dropdown closes before showing confirmation
    setTimeout(() => {
      dialog.confirmation(
        'Sign Out',
        'Are you sure you want to Sign out?',
        'Confirm',
        'Cancel',
        handleLogout,
        () => {
          // Cancel action - do nothing, just close the dialog
        }
      )
    }, 100)
  }

  // Close dropdowns when clicking outside
  const handleClickOutside = (event: MouseEvent) => {
    if (!event.target) return
    
    // Close all dropdowns when clicking outside
    closeAllDropdowns()
  }

  // Close dropdowns on route change
  watch(
    () => router.currentRoute.value.path,
    () => {
      closeAllDropdowns()
      isMobileMenuOpen.value = false
    }
  )

  const handleImageError = (event: Event) => {
    const target = event.target as HTMLImageElement
    if (target) {
      target.src = ''
      target.classList.add('bg-blue-50')
      
      const parent = target.parentElement
      if (parent) {
        parent.classList.add('fallback-avatar')
      }
    }
  }

  // Initialize app
  onBeforeMount(async () => {
    try {
      // Font Awesome setup
      const fontAwesomeLink = document.createElement('link')
      fontAwesomeLink.rel = 'stylesheet'
      fontAwesomeLink.href =
        'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css'
      document.head.appendChild(fontAwesomeLink)

      // Set ready state immediately to show loading spinner
      isReady.value = true

      // Initialize auth
      await auth.initialize()

      if(auth.user?.role?.toLowerCase() === "corporate") {
        isCorporate.value = true
      }
    } catch (error) {
      console.error('App initialization failed:', error)
    } finally {
      isLoading.value = false
    }
  })

  // Watch for auth initialization
  watch(
    () => auth.isInitialized,
    newValue => {
      if (newValue) {
        isLoading.value = false
      }
    },
    { immediate: true }
  )

  // Add these watchers after the existing variable declarations
  watch(
    () => auth.user,
    (newUser) => {
      if (newUser) {
        // Update corporate status whenever user changes
        isCorporate.value = (newUser as any)?.accessLevel?.toLowerCase() === "corporate"
        evaluateCrispGate()
      } else {
        isCorporate.value = false
        pushCrispCommand(['do', 'session:reset'])
        pushCrispCommand(['set', 'user:email', null])
        pushCrispCommand(['set', 'user:nickname', null])
        lastCrispUserId.value = null
      }
    },
    { immediate: true } // This will run immediately on component creation
  )
  
  // Handle impersonation login
  const handleImpersonationLogin = async (jwtToken: string) => {
    try {

      
      // Set global loading state for JWT login
      window.jwtLoginLoading = true
      
      // Let loginWithJWT handle the logout and login process

      const response = await auth.loginWithJWT(jwtToken)
      
  
      
      // Verify that login was successful
      if (!auth.isAuthenticated || !auth.user) {
        throw new Error('Login failed - user not authenticated after loginWithJWT')
      }
      
      // Show success message
      const userName = response.user.firstName + ' ' + response.user.lastName
      snackbarStore.showSuccess(`Impersonating ${userName}`)
      
      // Clean up URL
      const newUrl = new URL(window.location.href)
      newUrl.searchParams.delete('token')
      window.history.replaceState({}, '', newUrl.toString())
      
      // Clear the loading state
      window.jwtLoginLoading = false
      
      // Redirect to loans using Vue Router (same as regular login)
      await router.push('/loans')
      
    } catch (error: any) {
      console.error('App.vue: Impersonation login failed:', error)
      console.error('App.vue: Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status
      })
      snackbarStore.showError('Impersonation failed: ' + (error.message || 'Invalid token'))
      
      // Clear the loading state on error
      window.jwtLoginLoading = false
      
      // Clean up URL on error
      const newUrl = new URL(window.location.href)
      newUrl.searchParams.delete('token')
      window.history.replaceState({}, '', newUrl.toString())
    }
  }

  const handleChannelSelection = async (channel: MeridianPortalModeType) => {
    // Find the channel name from the channelList
    const selectedChannel = channelList.value.find(c => c.value === channel)
    const secretCompany = localStorage.getItem('secretCompany') || ''
    const company = localStorage.getItem('company') || ''
    switchingToChannel.value = selectedChannel?.name || 'Channel'
    
    isSwitchingPortalMode.value = true
    closeAllDropdowns()
    
    try {
      await portalModeStore.updatePortalMode(
        channel,
        secretCompany,
        company
      )
      
      // Reload the page to reflect the new channel
      window.location.reload()
    } catch (error: any) {
      console.error('Error switching channel:', error)
      isSwitchingPortalMode.value = false
      
      // Check if the error response contains the specific message
      if (error.response?.data?.message === 'Company ID is not correct' || 
          error.message?.includes('Company ID is not correct')) {
        await auth.logout()
        await router.push('/login')
      }
    } finally {
      isSwitchingPortalMode.value = false
    }
  }

  const getCurrentRoleLabel = () => {
    return tpoPortalRoleStore.getCurrentRole()
  }

  const getCurrentRoleIcon = () => {
    if (auth.user?.tpoPortalRole === PortalRoleType.PROCESSOR_MANAGER) {
      return 'fas fa-users-cog'
    }
    return 'fas fa-user-tie'
  }

  const handleRoleSwitch = async (userId: string, newRole: PortalRoleType) => {
    if (!userId || isSwitchingRole.value) {
      return
    }

    // Don't switch if already on this role
    if (tpoPortalRoleStore.getCurrentRole() === newRole) {
      isRoleDropdownOpen.value = false
      closeAllDropdowns()
      return
    }

    isSwitchingRole.value = true
    isRoleDropdownOpen.value = false
    closeAllDropdowns()

    try {
      dialog.loading('Switching role', 'Please wait while we switch your role')
      await tpoPortalRoleStore.switchPortalRole(userId, newRole)
      dialog.close()
      
      // Navigate based on role
      switch (newRole) {
        case PortalRoleType.BROKER:
          await router.push(`/loans`)
          break
        case PortalRoleType.PROCESSOR_MANAGER:
          await router.push(`/team-loans`)
          break
      }
    } catch (error: any) {
      console.error('Error switching role:', error)
      dialog.close()
      dialog.error('Error switching role', error.response?.data?.message || error.message || 'An error occurred while switching your role')
    } finally {
      isSwitchingRole.value = false
    }
  }

  const fetchFreshUserAndCompany = async () => {
    try {
      const company = JSON.parse(localStorage.getItem('company') || '{}')
      const user = JSON.parse(localStorage.getItem('user') || '{}')

      if (user._id && company._id) {
        const response = await userCompanyService.fetchFreshUserAndCompany(user._id, company._id)
        if (response.success && response.data) {
          localStorage.setItem('user', JSON.stringify(response.data.user))
          localStorage.setItem('company', JSON.stringify(response.data.company))
        }
      }
    } catch (error) {
      console.warn('Failed to fetch fresh user and company data:', error)
      // Continue with cached data
    }
  }

  onMounted(async () => {
    await portalModeStore.fetchPortalMode()
    await fetchFreshUserAndCompany()
    try {
      const company = JSON.parse(localStorage.getItem('company') || '{}')
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      if (user) {
        const portalRole = await tpoPortalRoleStore.fetchPortalRole(user._id)
        if (portalRole === PortalRoleType.PROCESSOR_MANAGER) {
          tpoPortalRoleStore.setCurrentSelection(portalRole)
        }
      }

      await fundedLoanAccessStore.setHasFundedLoanAccess(user)
      await multipleCompanyAccessStore.setHasMultipleCompanyAccess(user)
      await portalModeStore.setAvailableChannels(company)
    } catch (error: any) {
      console.error('Error fetching portal mode:', error)
    }
  })

  // Add and remove click event listeners
  onMounted(async () => {
    document.addEventListener('click', handleClickOutside)
    
    try {
      const applyFlags = () => {
        crispEnableAllEnabled.value = !!hasFeature('crisp_enable_all')

        const maintenanceEnabled = !!hasFeature('system_maintenance')
        const maintenanceRaw = getValue('system_maintenance') as unknown
        const maintenanceValue = (
          maintenanceRaw === true ||
          maintenanceRaw === 'true' ||
          maintenanceRaw === 1 ||
          maintenanceRaw === '1'
        )
        showSystemMaintenance.value = maintenanceEnabled && maintenanceValue

        if (auth.isAuthenticated && auth.user) {
          evaluateCrispGate()
        }
      }

      await initFeatureFlags()
      // Apply immediately after init to get current flags
      applyFlags()
      crispHoursIntervalId = setInterval(() => {
        evaluateCrispGate()
      }, 60 * 1000)
      // Subscribe for future changes
      onChange(() => applyFlags())
    } catch (e) {
      console.error('Failed to initialize Feature Flags:', e)
    }
    // Check for JWT token in URL for impersonation
    const urlParams = new URLSearchParams(window.location.search)
    const jwtToken = urlParams.get('token')
    
    if (jwtToken) {
      // Redirect to impersonation loading page with token preserved
      router.push({
        path: '/impersonation-loading',
        query: { token: jwtToken }
      })
    }
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
    if (crispHoursIntervalId) {
      clearInterval(crispHoursIntervalId)
      crispHoursIntervalId = null
    }
  })
</script>

<style lang="scss">
  :root {
    // Light mode variables — rebrand: purple #3D1951, green #29B533, white #FFFFFF
    --primary: #3D1951;
    --primary-hover: #0b0a0c;
    --ameritrust-primary: #3D1951;
    --ameritrust-primary-rgb: 61, 25, 81;
    --secondary: #29B533;
    --ameritrust-secondary: #29B533;
    --ameritrust-secondary-rgb: 41, 181, 51;
    --ameritrust-error: #DC2626;
    --ameritrust-error-rgb: 220, 38, 38;
    --danger: #DC2626;
    --text-color: #1a1a1a;
    --reverse-text-color: #FFFFFF;
    --background-color: #f8f9fa;
    --surface-ground: #f7f8f9;
    --surface-card: #FFFFFF;
    --surface-border: #e9ecef;
    --surface-hover: #f8f9fa;
    --text-secondary: #6c757d;
    --input-color: #fff;
    --skeleton-color: #dbdfe3;
    --skeleton-ground: #ebedef;
    --input-background: #fff;
    --card-background: #fff;
    --checkbox-color: #2d354c;
    --property-border: #e2e8f0;
    --shadow-color: rgba(0, 0, 0, 0.1);
    --has-errors-background: #f8d7da;
  }

  :root[data-theme='dark'] {
    // Dark mode — brand purple #3D1951 as the canvas, with stepped purple
    // elevations so cards/inputs still separate from the page background.
    --primary: #C9A4E8;
    --primary-hover: #D9BFF0;
    --ameritrust-primary: #C9A4E8;
    --ameritrust-primary-rgb: 201, 164, 232;
    --secondary: #4ADE65;
    --ameritrust-secondary: #4ADE65;
    --ameritrust-secondary-rgb: 74, 222, 101;
    --ameritrust-error: #F87171;
    --ameritrust-error-rgb: 248, 113, 113;
    --danger: #F87171;
    --text-color: #f5f0f8;
    --reverse-text-color: #3D1951;
    --background-color: #3D1951;
    --surface-ground: #3D1951;
    --surface-card: #4A2470;
    --surface-border: #5E3380;
    --surface-hover: #553070;
    --text-secondary: #C9A4E8;
    --input-color: #2A1138;
    --skeleton-color: #553070;
    --skeleton-ground: #2A1138;
    --input-background: #2A1138;
    --card-background: #4A2470;
    --checkbox-color: #fff;
    --property-border: #5E3380;
    --shadow-color: rgba(0, 0, 0, 0.35);
    --has-errors-background: #4a1a2a;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell,
      'Open Sans', 'Helvetica Neue', sans-serif;
    color: var(--text-color);
    line-height: 1.6;
    margin-top: 64px;
    background-color: var(--background-color) !important;
    transition:
      background-color 0.0s ease,
      color 0.3s ease;
  }

  .app-wrapper {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    padding-top: 64px; /* Height of navbar */
    margin-top: 0;
    padding-top: 0;
  }

  /* Co-branded logo needs room — collapse desktop nav earlier than the old 950px cutover */
  $nav-collapse: 1280px;

  /* Navbar Styles - Redesigned */
  .navbar {
    top: 0;
    left: 0;
    right: 0;
    height: 100px;
    padding: 1.5rem 2rem 1rem;
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
    overflow: visible;
    
    &.login-page::before, &.forgot-password-page::before, &.register-page::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: url('./assets/images/bg.gif') no-repeat center center fixed;
      background-size: cover;
      opacity: 0.5; // Adjust this value to control darkness (lower = darker)
      z-index: -1;
    }

    &[data-theme='dark'] {
      &.login-page::before, &.forgot-password-page::before, &.register-page::before {
        opacity: 0.15; // Even darker for dark theme
        background: url('./assets/images/bg.gif') no-repeat center center fixed;
        background-size: cover;
      }
    }

    @media screen and (min-width: 1600px) {
      padding: 2rem 4rem 1rem;
    }
    
    @media (max-width: 768px) {
      padding: 0 1rem;
      background-color: var(--background-color);
      position: fixed; /* Make header fixed in mobile view */
      width: 100%;
      height: 64px;
      
      .logo {
        margin-top: 0;
        height: 40px;

        img {
          height: 40px;
          max-width: min(200px, 52vw);
        }
      }
    }
  }

  .logo-container {
    position: relative;
    margin-top: 0;
    height: 72px;
    flex-shrink: 1;
    min-width: 0;
  }

  .logo {
    margin-top: 0;
    height: 72px;
    display: flex;
    align-items: center;
    cursor: pointer;
    transition: opacity 0.2s ease;
    flex-shrink: 1;
    min-width: 0;
    
    &:hover {
      opacity: 0.8;
    }
    
    img {
      object-fit: contain;
      margin-left: 0;
      height: 72px;
      width: auto;
      max-width: min(320px, 44vw);
    }

    
    .light-logo {
      display: block;
    }
    
    .dark-logo {
      display: none;
    }
  }

  .company-switch-menu {
    position: absolute;
    top: calc(100% + 1rem);
    left: 0;
    min-width: 320px;
    max-width: 420px;
    width: max-content;
    background-color: var(--surface-card);
    border: 1px solid var(--surface-border);
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    z-index: 1000;
    animation: dropdownEnter 0.2s ease forwards;
    max-height: 500px;
    display: flex;
    flex-direction: column;
    padding: 0;
  }

  .company-menu-header {
    flex-shrink: 0;
    padding: 0.4rem 1.25rem;
    background-color: var(--surface-card);
    border-bottom: 1px solid var(--surface-border);
    
    .menu-header {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      color: var(--text-secondary);
      font-size: 0.875rem;
      font-weight: 500;
      
      .menu-header-icon {
        font-size: 1rem;
        color: var(--primary);
      }
    }
  }

  .company-menu-content {
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem;
    max-height: calc(500px - 80px);
    
    /* Custom scrollbar */
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background: var(--surface-border);
      border-radius: 3px;
      
      &:hover {
        background: var(--text-secondary);
      }
    }
  }

  .company-menu-loading,
  .company-menu-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem 1.5rem;
    color: var(--text-secondary);
    font-size: 0.875rem;
    gap: 0.75rem;
    
    i {
      font-size: 1.5rem;
      opacity: 0.5;
    }
  }

  .company-menu-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.875rem 1rem;
    margin-bottom: 0.375rem;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: var(--text-color);
    text-align: left;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    
    &:hover:not(:disabled) {
      background-color: var(--surface-border);
      border-color: var(--surface-border);
      transform: translateX(2px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }
    
    &:active:not(:disabled) {
      transform: translateX(0);
    }
    
    &:disabled {
      cursor: default;
      opacity: 1;
    }
    
    &.company-menu-item-active {
      background: #f3eaf8 !important;
      border-color: #5A2A75;
      box-shadow: 0 0 0 1px #5A2A75, 0 2px 8px rgba(61, 25, 81, 0.15);
      
      .company-menu-item-icon-wrapper {
        background: #5A2A75;
        color: white;
        box-shadow: 0 2px 6px rgba(97, 162, 208, 0.3);
        transform: scale(1.05);
      }
      
      .company-menu-item-name {
        color: #3D1951;
        font-weight: 600;
      }
      
      .company-menu-item-nmls {
        color: #4a90a4;
      }
      
      .company-menu-item-check {
        opacity: 1;
        transform: scale(1);
        background: #5A2A75;
        box-shadow: 0 2px 6px rgba(97, 162, 208, 0.4);
      }
    }
  }

  .company-menu-item-content {
    display: flex;
    align-items: center;
    gap: 0.875rem;
    flex: 1;
    min-width: 0;
    position: relative;
    z-index: 1;
  }

  .company-menu-item-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 8px;
    background: var(--surface-hover);
    color: var(--primary);
    font-size: 1rem;
    flex-shrink: 0;
    transition: all 0.2s ease;
  }

  .company-menu-item-text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
    flex: 1;
  }

  .company-menu-item-name {
    font-size: 0.9375rem;
    font-weight: 500;
    color: var(--text-color);
    line-height: 1.4;
    transition: color 0.2s ease;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .company-menu-item-nmls {
    font-size: 0.75rem;
    color: var(--text-secondary);
    font-weight: 400;
    line-height: 1.3;
  }

  .company-menu-item-check {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    background: var(--primary);
    color: white;
    font-size: 0.75rem;
    flex-shrink: 0;
    opacity: 0;
    transform: scale(0.8);
    transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    z-index: 1;
    box-shadow: 0 2px 6px rgba(0, 113, 227, 0.3);
  }

  /* Desktop Navigation */
  .desktop-nav {
    display: flex;
    align-items: center;
    height: 100%;
    flex: 1 1 auto;
    min-width: 0;
    justify-content: flex-end;
    gap: 0.125rem;

    @media screen and (max-width: $nav-collapse) {
      display: none;
    }
  }

  .nav-item {
    height: 64px;
    padding: 0 0.65rem;
    display: flex;
    align-items: center;
    color: var(--text-color);
    font-weight: 500;
    font-size: 0.9375rem;
    text-decoration: none;
    position: relative;
    white-space: nowrap;
    flex-shrink: 0;

    @media screen and (min-width: 1440px) {
      padding: 0 1rem;
      font-size: 1rem;
    }

    body:not(.login-page):not(.forgot-password-page):not(.register-page) & {
      transition: all 0.2s;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 3px;
      background-color: var(--primary);
      transform: scaleX(0);

      body:not(.login-page):not(.forgot-password-page) & {
        transition: transform 0.2s;
      }
    }
    
    &:hover {
      color: var(--primary);

      &::after {
        transform: scaleX(0.5);
      }
      // background-color: var(--surface-hover);
    }
    
    &.router-link-active {
      color: var(--text-color);
      font-weight: 600;
      
      &::after {
        transform: scaleX(1);
      }
    }
  }

  /* Dropdown Navigation Items */
  .nav-item-dropdown {
    position: relative;
    height: 64px;
    display: flex;
    align-items: center;
  }

  .nav-dropdown-trigger {
    height: 52px !important;
    padding: 0 0.65rem;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    color: var(--text-color);
    font-weight: 500;
    font-size: 0.9375rem;
    background: none;
    border: none;
    cursor: pointer;
    position: relative;
    transition: all 0.2s;
    white-space: nowrap;
    flex-shrink: 0;

    @media screen and (min-width: 1440px) {
      padding: 0 1rem;
      font-size: 1rem;
      gap: 0.5rem;
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 3px;
      background-color: var(--primary);
      transform: scaleX(0);
      transition: transform 0.2s;
    }
    
    &:hover {
      color: var(--primary);
      // background-color: var(--surface-hover);

      &::after {
        transform: scaleX(0.5);
      }
    }
    
    &.active {
      color: var(--primary);
      
      &::after {
        transform: scaleX(1);
      }
    }
    
    i {
      font-size: 0.75rem;
    }
  }

  .nav-dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    width: 240px;
    background-color: var(--surface-card);
    border-radius: 8px;
    box-shadow: 0 4px 12px var(--shadow-color);
    border: 1px solid var(--surface-border);
    overflow: hidden;
    z-index: 100;
    transform-origin: top center;
    animation: dropdownEnter 0.2s ease forwards;
  }

  @keyframes dropdownEnter {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .menu-header {
    padding: 0.75rem 1rem;
    color: var(--text-secondary);
    font-size: 0.875rem;
    font-weight: 500;
  }

  .menu-divider {
    height: 1px;
    background-color: var(--surface-border);
    margin: 0.25rem 0;
  }

  .menu-item {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    color: var(--text-color);
    text-decoration: none;
    transition: background-color 0.2s;
    background: none;
    border: none;
    width: 100%;
    text-align: left;
    cursor: pointer;
    
    &:hover:not(:disabled) {
      background-color: rgba(var(--ameritrust-primary-rgb, 61, 25, 81), 0.16);
      color: var(--primary);
    }
    
    &.active {
      background-color: rgba(var(--primary-rgb, 0, 31, 96), 0.1);
      color: var(--primary);
      font-weight: 500;
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    
    &.disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    
    .menu-icon {
      width: 1.25rem;
      margin-right: 0.75rem;
      color: var(--primary);
      text-align: center;
    }
  }

  .channel-menu-item {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    padding: 0.875rem 1rem;
    color: var(--text-color);
    text-decoration: none;
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 6px;
    margin: 0.125rem 0;
    
    &:hover {
      background-color: var(--surface-hover);
      color: var(--primary);
    }
    
    &:active {
      background-color: var(--surface-active);
      transform: translateY(1px);
    }
    
    .menu-icon {
      width: 1.25rem;
      height: 1.25rem;
      margin-right: 0.75rem;
      color: var(--primary);
      text-align: center;
      flex-shrink: 0;
    }
    
    span {
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.25;
    }
  }

  .logout-item {
    color: var(--secondary);
    background: none;
    border: none;
    width: 100%;
    text-align: left;
    cursor: pointer;
    
    .menu-icon {
      color: var(--secondary);
    }
    
    &:hover {
      background-color: rgba(220, 38, 38, 0.1);
    }
  }

  /* Portal Mode Dropdown - Simple Border Enhancement */
  .portal-mode-trigger {
    border: 2px solid var(--primary) !important;
    border-radius: 6px !important;
    background: linear-gradient(135deg, rgba(var(--primary-rgb, 61, 25, 81), 0.1) 0%, rgba(var(--primary-rgb, 61, 25, 81), 0.05) 100%) !important;
    max-width: 9.5rem;

    > span {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  /* Mobile Portal Mode Dropdown Styling */
  .mobile-dropdown .portal-mode-trigger {
    background: linear-gradient(135deg, rgba(var(--primary-rgb, 59, 130, 246), 0.08) 0%, rgba(var(--primary-rgb, 59, 130, 246), 0.03) 100%) !important;
    border: 1px solid var(--primary) !important;
    border-radius: 8px !important;
    padding: 0.75rem 1rem !important;
    margin: 0.5rem 0 !important;
    font-weight: 600 !important;
    color: var(--primary) !important;
    
    .portal-mode-icon {
      color: var(--primary) !important;
      font-size: 0.875rem !important;
      margin-right: 0.5rem !important;
    }
    
    &:hover {
      background: linear-gradient(135deg, rgba(var(--primary-rgb, 59, 130, 246), 0.12) 0%, rgba(var(--primary-rgb, 59, 130, 246), 0.06) 100%) !important;
      border-color: var(--primary-dark, #1e40af) !important;
    }
    
    &.active {
      background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark, #1e40af) 100%) !important;
      color: white !important;
      border-color: var(--primary-dark, #1e40af) !important;
      
      .portal-mode-icon {
        color: white !important;
      }
    }
  }

  .mobile-dropdown .mobile-dropdown-content {
    border: 1px solid var(--primary) !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15) !important;
    margin-top: 0.25rem !important;
    
    .mobile-menu-subitem {
      display: flex !important;
      align-items: center !important;
      justify-content: flex-start !important;
      text-align: left !important;
      padding: 0.75rem 1rem !important;
      border-radius: 6px !important;
      margin: 0.25rem 0.5rem !important;
      width: calc(100% - 1rem) !important;
      
      &:hover {
        background: rgba(var(--primary-rgb, 59, 130, 246), 0.08) !important;
        color: var(--primary) !important;
      }
      
      .menu-icon {
        color: var(--primary) !important;
        margin-right: 0.75rem !important;
        flex-shrink: 0 !important;
      }
      
      span {
        text-align: left !important;
        flex: 1 !important;
      }
    }
  }

  /* User Dropdown */
  .user-dropdown-trigger {
    width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 50%;
    border: none;
    background: none;
    cursor: pointer;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    
    &:hover {
      background-color: var(--surface-hover);
    }
    
    &.active {
      border: 2px solid var(--primary);
    }
  }

  .user-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    overflow: hidden;
    background-color: var(--surface-hover);
    display: flex;
    align-items: center;
    justify-content: center;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    i {
      color: var(--primary);
      font-size: 1rem;
    }
  }

  .user-dropdown-menu {
    width: 280px;
    right: 0;
    left: auto;
  }

  /* Role Switcher Styles */
  .role-switcher-container {
    padding: 0.75rem 1rem;
  }

  .role-switcher-header {
    margin-bottom: 0.5rem;
  }

  .role-switcher-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .role-switcher-dropdown {
    position: relative;
  }

  .role-switcher-trigger {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0.625rem 0.75rem;
    background: var(--surface-card);
    border: 1px solid var(--surface-border);
    border-radius: 6px;
    color: var(--text-color);
    text-align: left;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.875rem;
    font-weight: 500;

    &:hover:not(:disabled) {
      background-color: var(--surface-hover);
      border-color: var(--primary);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &.active {
      border-color: var(--primary);
    }

    .menu-icon {
      width: 1.25rem;
      margin-right: 0.75rem;
      color: var(--primary);
      text-align: center;
      flex-shrink: 0;
    }

    span {
      flex: 1;
    }

    .role-chevron {
      margin-left: auto;
      font-size: 0.75rem;
      color: var(--text-secondary);
      transition: transform 0.2s ease;
    }
  }

  .role-switcher-menu {
    position: absolute;
    top: calc(100% + 0.25rem);
    left: 0;
    right: 0;
    background-color: var(--surface-card);
    border: 1px solid var(--surface-border);
    border-radius: 6px;
    box-shadow: 0 4px 12px var(--shadow-color);
    overflow: hidden;
    z-index: 101;
    animation: dropdownEnter 0.2s ease forwards;
  }

  .role-switcher-option {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0.625rem 0.75rem;
    background: none;
    border: none;
    color: var(--text-color);
    text-align: left;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    font-size: 0.875rem;
    font-weight: 500;

    &:hover:not(:disabled) {
      background-color: var(--surface-hover);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &.active {
      background-color: rgba(var(--primary-rgb, 0, 31, 96), 0.1);
      color: var(--primary);
    }

    .menu-icon {
      width: 1.25rem;
      margin-right: 0.75rem;
      color: var(--primary);
      text-align: center;
      flex-shrink: 0;
    }

    span {
      flex: 1;
    }

    .role-check {
      margin-left: auto;
      color: var(--primary);
      font-size: 0.875rem;
    }
  }

  .user-info {
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .user-avatar-large {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    overflow: hidden;
    background-color: var(--surface-hover);
    display: flex;
    align-items: center;
    justify-content: center;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    i {
      color: var(--primary);
      font-size: 1.25rem;
    }
  }

  .user-details {
    overflow: hidden;
  }

  .user-name {
    font-weight: 500;
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .user-email {
    font-size: 0.875rem;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Sign In Button */
  .sign-in-btn {
    height: 38px;
    padding: 0 1.50rem;
    color: var(--ameritrust-primary);
    font-family: Inter;
    border-radius: 10px;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    margin-left: 0.5rem;
    transition: all 0.2 ease-in-out;

    &[data-theme='dark'] {
      color: white !important;
    }

    &:hover {
      opacity: 0.9;
    }
  }

  /* Theme Toggle */
  .theme-toggle-btn {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: none;
    background: none;
    color: var(--text-color);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin-left: 0.5rem;

    body:not(.login-page):not(.forgot-password-page) & {
      transition: all 0.2s;
    }

    &:hover {
      background-color: var(--surface-hover);
    }
    
    i {
      font-size: 1.125rem;
    }
  }

  /* Mobile Navigation Styles */
  .mobile-nav {
    display: none;
    align-items: center;
    flex-shrink: 0;

    @media (max-width: $nav-collapse) {
      display: flex;
    }
  }

  .mobile-logo {
    display: flex;
    top: 1rem;
    left: 1.25rem !important;
    position: absolute;

    .mobile-light-logo, .mobile-dark-logo {
      height: 38px;
      width: auto;
      max-width: min(210px, 62vw);
      object-fit: contain;
    }

    .mobile-light-logo {
      display: block;
    }

    .mobile-dark-logo {
      display: none;
    }
  }

  .mobile-menu-btn {
    width: 40px;
    height: 40px;
    z-index: 10000;
    border-radius: 50%;
    border: none;
    background: none;
    color: var(--text-color);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;

    @media screen and (max-width: $nav-collapse) {
      margin-top: 0;
    }
    
    &:hover {
      background-color: var(--surface-hover);
    }
    
    i {
      font-size: 1.25rem;
    }
  }

  main {
    flex: 1;
    width: 100%;
    height: calc(100vh - 64px); /* Viewport height minus navbar height */
    overflow-y: auto;
    position: relative;

    &.nav-hidden {
      height: 100vh;
    }
    z-index: 1;
  }

  .footer {
    background: var(--surface-card);
    color: var(--text-color);
    padding: 3rem 0 2rem;
    margin-top: auto;
    border-top: 1px solid var(--surface-border);

    .footer-content {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 2rem;
    }

    .footer-main {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 3rem;
      margin-bottom: 2rem;
    }

    .footer-col {
      h4 {
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 1.25rem;
        color: var(--primary);
      }

      .footer-logo {
        height: 48px;
        width: auto;
        max-width: 260px;
        object-fit: contain;
        margin: 0;
        padding: 0;
        line-height: 0;
        background: transparent;
        border-radius: 0;
      }

      /* Only one logo visible at a time — default to light */
      .footer-logo.light-logo {
        display: block;
      }

      .footer-logo.dark-logo {
        display: none;
      }

      .address {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        font-size: 0.875rem;
        color: var(--text-secondary);
        line-height: 1.2;
        margin: 2rem 0 0;
        padding: 0;

        i {
          color: var(--primary);
          margin-top: 0.25rem;
        }
      }

      .contact-links {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;

        a {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: var(--text-secondary);
          text-decoration: none;
          font-size: 0.875rem;
          transition: color 0.2s;

          i {
            color: var(--primary);
          }

          &:hover {
            color: var(--primary);
          }
        }
      }

      .license-items {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;

        .license-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: var(--text-secondary);

          i {
            color: var(--primary);
          }
        }
      }
    }

    .footer-divider {
      height: 1px;
      background: var(--surface-border);
      margin: 2rem 0;
    }

    .footer-legal {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .legal-left {
        display: flex;
        align-items: center;
        gap: 1.5rem;

        .equal-housing-logo {
          height: 32px;
        }

        .nmls-link {
          color: var(--primary);
          text-decoration: none;
          font-size: 0.875rem;
          font-weight: 500;

          &:hover {
            text-decoration: underline;
          }
        }
      }

      .copyright {
        font-size: 0.875rem;
        color: var(--text-secondary);
      }
    }
  }

  @media (max-width: 1024px) {
    .footer {
      .footer-main {
        grid-template-columns: 1fr;
        gap: 2rem;
        text-align: center;
      }

      .footer-col {
        .footer-logo {
          margin: 0 auto 1.25rem;
        }

        .address {
          justify-content: center;
        }

        .contact-links a {
          justify-content: center;
        }

        .license-items .license-item {
          justify-content: center;
        }
      }

      .footer-legal {
        flex-direction: column;
        gap: 1rem;
        text-align: center;

        .legal-left {
          flex-direction: column;
          gap: 1rem;
        }
      }
    }
  }

  @media (max-width: 768px) {
    body {
      margin-top: 64px;
    }

    .navbar {
      top: 0;
      padding: 0.75rem 1rem;

      .nav-links {
        display: none; /* Will add hamburger menu */
      }
    }

    .footer {
      padding: 2rem 0 1.5rem;

      .footer-content {
        padding: 0 1rem;
      }
    }
  }

  .v-application {
    background-color: var(--background-color);
  }

  .page-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 16px;
  }

  .snackbar {
    position: fixed;
    bottom: 2rem;
    right: 2rem;
    padding: 1rem 1.5rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    z-index: 9999;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    min-width: 300px;
    max-width: 500px;
    animation: slideIn 0.3s ease;
    &.success {
      background: #10b981;
      color: white;
    }
    &.error {
      background: #ef4444;
      color: white;
    }
    &.warning {
      background: #f59e0b;
      color: white;
    }
    &.info {
      background: #3b82f6;
      color: white;
    }

    .snackbar-icon {
      font-size: 1.25rem;
    }

    .close-btn {
      margin-left: auto;
      background: none;
      border: none;
      color: currentColor;
      opacity: 0.8;
      cursor: pointer;
      padding: 0.25rem;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 1;
      }
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition:
      opacity 0.3s ease,
      transform 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(1rem);
  }

  @keyframes slideIn {
    from {
      transform: translateY(1rem);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .theme-toggle {
    background: none;
    border: none;
    color: var(--text-color);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    margin: 0 0 0 0.25rem;
    padding: 0;
    box-sizing: border-box;

    &:hover {
      background: var(--surface-hover);
    }

    i {
      font-size: 1.125rem;
    }
  }

  .profile-dropdown {
    background: none;
    border: none;
    color: var(--text-color);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    margin: 0;
    padding: 0;
    box-sizing: border-box;

    &:hover {
      background: var(--surface-hover);
    }

    i {
      font-size: 1.125rem;
    }
  }

  :root[data-theme='dark'] {
    .navbar .logo, .mobile-menu-panel .mobile-logo {
      .light-logo,
      .mobile-light-logo {
        display: none;
      }

      .dark-logo,
      .mobile-dark-logo {
        display: block;
      }
    }

    .footer {
      .address,
      .contact-links a,
      .license-items .license-item,
      .copyright {
        color: var(--text-secondary);
      }

      .footer-col h4 {
        color: #fff;
      }

      .contact-links a:hover {
        color: #fff;
      }

      .nmls-link {
        color: #fff;

        &:hover {
          opacity: 0.9;
        }
      }
    }

    .navbar .logo,
    .footer-col {
      .light-logo {
        display: none;
      }

      .dark-logo {
        display: block;
      }

      .footer-logo.light-logo {
        display: none;
      }

      .footer-logo.dark-logo {
        display: block;
      }
    }
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--surface-border);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .loading-spinner-small {
    width: 16px;
    height: 16px;
    border: 2px solid var(--surface-border);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .custom-dropdown {
    position: relative;
    display: inline-block;
  }

  .dropdown-arrow {
    margin-left: 5px;
    font-size: 12px;
  }

  .dropdown-menu {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 8px 0;
    margin-top: 4px;
    min-width: 150px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }

  .dropdown-menu.show {
    display: block;
  }

  .dropdown-item {
    display: block;
    padding: 8px 16px;
    color: #333;
    text-decoration: none;
  }

  .dropdown-item:hover {
    background-color: #f5f5f5;
  }

  // Update mobile menu button styles
  .mobile-menu-btn {
    @apply hidden;
    color: var(--text-color);
    padding: 0.5rem;
    margin-left: 0.5rem;
    @media (max-width: $nav-collapse) {
      @apply flex items-center justify-center;
    }
  }

  // Update existing navbar styles
  .navbar {
    position: relative;
    .nav-links {
      @media (max-width: $nav-collapse) {
        position: absolute;
        top: 100%;
        right: 0;
        width: 240px;
        background: var(--background-color);
        padding: 1rem;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
        z-index: 50;
        border-radius: 0.5rem;
        margin-top: 0.5rem;

        &.mobile-active {
          display: flex;
        }

        .nav-link {
          width: 100%;
          padding: 0.5rem 0;
        }
      }

      @media (min-width: #{$nav-collapse + 1}) {
        display: flex;
        align-items: center;
      }
    }
  }

  // Update transitions and animations
  @keyframes rotate-in {
    0% {
      transform: rotate(0deg) scale(1);
    }
    50% {
      transform: rotate(180deg) scale(1.2);
    }
    100% {
      transform: rotate(360deg) scale(1);
    }
  }

  @keyframes rotate-out {
    0% {
      transform: rotate(360deg) scale(1);
    }
    50% {
      transform: rotate(180deg) scale(0.8);
    }
    100% {
      transform: rotate(0deg) scale(1);
    }
  }

  .rotate-in {
    animation: rotate-in 0.4s ease-out forwards;
  }

  .rotate-out {
    animation: rotate-out 0.4s ease-out forwards;
  }

  // Update mobile menu styles
  .mobile-menu {
    @apply backdrop-blur-lg bg-white/90 dark:bg-gray-900/90;
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06),
      0 0 0 1px rgba(255, 255, 255, 0.1);
    .nav-link {
      @apply transition-all duration-200;

      &:hover {
        @apply translate-x-2;
        text-shadow: 0 0 20px rgba(0, 113, 227, 0.3);
      }

      i {
        @apply transition-all duration-200;
      }

      &:hover i {
        @apply scale-110 text-[#0071e3];
        filter: drop-shadow(0 0 8px rgba(0, 113, 227, 0.4));
      }
    }
  }

  // Update mobile menu button
  .mobile-menu-btn {
    @apply transition-transform duration-200;
    &:hover {
      @apply scale-110;
    }
    &:active {
      @apply scale-95;
    }
    i {
      @apply transition-all duration-300;
      transform-origin: center;
    }
  }

  // Update keyframe animations
  @keyframes slideIn {
    0% {
      opacity: 0;
      visibility: visible;
      transform: translateY(-20px) scale(0.95);
    }
    100% {
      opacity: 1;
      visibility: visible;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes slideOut {
    0% {
      opacity: 1;
      visibility: visible;
      transform: translateY(0) scale(1);
    }
    100% {
      opacity: 0;
      visibility: hidden;
      transform: translateY(-20px) scale(0.95);
    }
  }

  // Update animation classes
  .animate-in {
    animation: slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  .animate-out {
    animation: slideOut 0.2s cubic-bezier(0.7, 0, 0.84, 0) forwards;
  }

  // Separate mobile and desktop nav styles
  .nav-links {
    // Desktop styles
  }

  .nav-links-mobile {
    position: absolute;
    top: 100%;
    right: 0;
    width: 200px;
    background: var(--background-color);
    padding: 0.75rem 0.75rem;
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06),
      0 0 0 1px rgba(255, 255, 255, 0.1);
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;
    z-index: 50;
    border-radius: 0.5rem;
    margin-top: 0.5rem;
    display: flex;
    transform-origin: top right;
    backdrop-filter: blur(8px);
    
    .nav-link {
      width: 100%;
      transition: all 0.2s ease;
      text-align: right;
      padding: 0.5rem 0.75rem;
      font-size: 1rem;
      position: relative;
      
      &::after {
        content: '';
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%) scaleX(0);
        width: 3px;
        height: 70%;
        background-color: var(--primary);
        transition: transform 0.2s ease;
        border-radius: 2px;
        transform-origin: right;
      }

      &:hover {
        transform: translateX(-4px);
      }

      &.router-link-active {
        color: var(--primary) !important;
        background: var(--surface-hover);
        
        &::after {
          transform: translateY(-50%) scaleX(1);
        }
      }

      &.flex {
        flex-direction: row-reverse;
        justify-content: flex-start;

        i {
          margin-right: 0;
          margin-left: 0.5rem;
          font-size: 1rem;
        }
      }
    }
    .dropdown-menu {
      position: relative !important;
      right: auto !important;
      left: 0 !important;
      width: 90% !important;
      margin-right: auto;
      box-shadow: none;
      background: transparent;
      padding-right: 0;

      .nav-link {
        padding-left: 2rem;
        text-align: left;

        &:hover {
          transform: translateX(4px);
        }

        &.flex {
          flex-direction: row;

          i {
            margin-right: 0.5rem;
            margin-left: 0;
          }
        }
      }
    }
  }

  /* Add these new styles */
  .nav-item {
    position: relative;
    display: inline-flex;
    height: 100%;
    align-items: center;
  }

  .nav-links {
    .nav-item .nav-link {
      padding: 0.5rem 1rem;
      height: 100%;
      display: flex;
      align-items: center;
    }
  }

  /* Mobile nav item styles */
  .nav-item-mobile {
    position: relative;
    width: 100%;

    .nav-link {
      height: auto;
      padding: 0.75rem 1rem;
      width: 100%;
    }
  }

  /* New stable dropdown styles */
  .stable-dropdown {
    position: relative;
    display: inline-flex;
    align-items: center;
    margin: 0;
    height: 38px;
    box-sizing: border-box;
    width: auto;
    /* Fix width to prevent layout shifts */
    min-width: 107px;
  }

  .company-dropdown-btn {
    background: transparent;
    color: var(--text-color);
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-weight: 500;
    font-size: 0.9375rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 38px;
    width: 100%;
    box-sizing: border-box;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 0.2s;
    line-height: 1.5;

    /* Prevent text from wrapping */
    text-align: center;
    overflow: visible;

    &:hover {
      background: var(--surface-hover);
    }

    &[data-state="open"] {
      background: var(--surface-hover);
    }

    span {
      display: inline-block;
      position: relative;
      margin-right: 0.5rem; /* Fixed spacing between text and icon */
    }

    .icon-chevron {
      font-size: 0.7rem;
      transition: transform 0.2s ease;
      display: inline-flex;
      flex: 0 0 auto;
      /* Fixed width for icon to prevent layout shifts */
      width: 8px;
      text-align: center;
    }

    .icon-chevron.rotate {
      transform: rotate(180deg);
    }
  }

  .dropdown-content {
    position: fixed;
    width: 14rem;
    background: var(--surface-card);
    border-radius: 0.75rem;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    border: 1px solid var(--surface-border);
    padding: 0.25rem;
    margin-top: 0.5rem;
    z-index: 100;
    box-sizing: border-box;
  }

  .dropdown-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--text-secondary);
    padding: 0.625rem 0.75rem;
  }

  .dropdown-separator {
    height: 1px;
    background-color: var(--surface-border);
    margin: 0.25rem 0;
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    padding: 0.625rem 0.75rem;
    border-radius: 0.5rem;
    transition: background-color 0.15s;
    cursor: pointer;
    font-size: 0.875rem;
    
    &:hover {
      background-color: var(--surface-hover);
    }

    .item-icon {
      width: 1.25rem;
      margin-right: 0.75rem;
      font-size: 1rem;
      color: var(--primary);
      text-align: center;
    }
  }

  /* Mobile stable dropdown styles */
  .mobile-stable-dropdown {
    position: relative;
    width: 100%;
    margin-bottom: 0.25rem;
  }

  .mobile-company-dropdown-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    text-align: right;
    padding: 0.75rem 1rem;
    background: transparent;
    border: none;
    color: #000;
    font-size: 1rem;
    font-weight: 500;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }

    .icon-chevron {
      font-size: 0.7rem;
      transition: transform 0.2s ease;
    }

    .icon-chevron.rotate {
      transform: rotate(180deg);
    }
  }

  .mobile-dropdown-content {
    position: absolute;
    top: auto;
    right: auto;
    width: 14rem;
    margin-top: 0.5rem;
    background: white;
    border-radius: 0.75rem;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(0, 0, 0, 0.1);
    z-index: 100;
  }

  .mobile-dropdown-inner {
    padding: 0.5rem;
  }

  .mobile-dropdown-item {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    color: #1d1d1f;
    text-decoration: none;
    transition: background-color 0.15s;
    font-size: 0.875rem;
    
    &:hover {
      background-color: #f5f5f7;
    }

    .item-icon {
      width: 1.25rem;
      margin-right: 0.75rem;
      font-size: 1rem;
      color: #0071e3;
      text-align: center;
    }
  }

  /* Company dropdown container to ensure consistent spacing */
  .company-container {
    display: flex;
    align-items: center;
    height: 38px;
    padding: 0;
    margin: 0 0.35rem;
    box-sizing: border-box;
    position: relative;
  }

  /* Update dropdown behavior when opened */
  .nav-item.company-container {
    position: static;
  }

  /* Ensure dropdown stays in place when open */
  [data-state="open"] {
    position: relative;
    z-index: 101;
  }

  /* Position fixed for all dropdown content */
  [data-radix-popper-content-wrapper] {
    position: fixed !important;
  }

  /* Mobile Navigation Overlay */
  .mobile-menu-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9998;
    animation: fadeIn 0.2s ease forwards;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Mobile Menu Panel */
  .mobile-menu-panel {
    position: fixed;
    // top: 64px;
    top: 0;
    padding-top: 70px !important;
    right: 0;
    width: 300px;
    max-width: 100%;
    height: calc(100vh);
    background-color: var(--surface-card);
    border-bottom-left-radius: 1rem;
    z-index: 9999;
    padding: 1rem;
    overflow-y: auto;
    box-shadow: -2px 0 10px var(--shadow-color);
    transform: translateX(100%);
    transition: transform 0.3s ease;
    
    &.open {
      transform: translateX(0);
    }
  }

  .mobile-menu-item {
    display: flex;
    align-items: center;
    padding: 0.875rem 1rem;
    color: var(--text-color);
    text-decoration: none;
    font-weight: 500;
    border-radius: 8px;
    transition: background-color 0.2s;
    margin-bottom: 0.25rem;
    
    &:hover {
      background-color: var(--surface-hover);
    }
    
    .menu-icon {
      width: 1.25rem;
      margin-right: 0.75rem;
      color: var(--primary);
      text-align: center;
    }
    
    &.router-link-active {
      color: var(--primary);
      background-color: var(--surface-hover);
    }
  }

  .mobile-dropdown {
    margin-bottom: 0.25rem;
  }

  .mobile-dropdown-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.875rem 1rem;
    color: var(--text-color);
    background: none;
    border: none;
    font-weight: 500;
    text-align: left;
    cursor: pointer;
    border-radius: 8px;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: var(--surface-hover);
    }
    
    i {
      transition: transform 0.2s;
    }
  }

  .mobile-dropdown-content {
    // padding-left: 1rem;
    background-color: var(--surface-ground);
    border: 1px solid var(--primary-hover);
    margin-top: 0.25rem;
    animation: slideDown 0.2s ease forwards;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .mobile-menu-subitem {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    color: var(--text-color);
    text-decoration: none;
    border-radius: 8px;
    transition: background-color 0.2s;
    margin-bottom: 0.25rem;
    
    &:hover {
      background-color: var(--surface-hover);
    }
    
    .menu-icon {
      width: 1.25rem;
      margin-right: 0.75rem;
      color: var(--primary);
      text-align: center;
    }
    
    &.router-link-active {
      color: var(--primary);
      background-color: var(--surface-card);
    }
  }

  .mobile-menu-divider {
    height: 1px;
    background-color: var(--surface-border);
    margin: 1rem 0;
  }

  .mobile-user-section {
    margin-bottom: 1rem;
  }

  .mobile-user-info {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    margin-bottom: 1rem;
    background-color: var(--surface-hover);
    border-radius: 8px;
  }

  .mobile-user-avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    overflow: hidden;
    background-color: var(--surface-card);
    display: flex;
    align-items: center;
    justify-content: center;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    i {
      color: var(--primary);
      font-size: 1.25rem;
    }
  }

  .mobile-user-details {
    overflow: hidden;
    flex: 1;
  }

  .mobile-user-name {
    color: var(--text-color);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .mobile-user-email {
    font-size: 0.875rem;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .mobile-logout-btn {
    color: var(--secondary);
    background: none;
    border: none;
    width: 100%;
    text-align: left;
    cursor: pointer;
    
    .menu-icon {
      color: var(--secondary);
    }
    
    &:hover {
      background-color: rgba(219, 86, 86, 0.1);
    }
  }

  .mobile-sign-in {
    background-color: var(--ameritrust-primary) !important;
    color: white !important;
  }

  .mobile-theme-toggle {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0.875rem 1rem;
    margin-top: 1rem;
    background: none;
    border: none;
    color: var(--text-color);
    font-weight: 500;
    text-align: left;
    cursor: pointer;
    border-radius: 8px;

    body:not(.login-page):not(.forgot-password-page) & {
      transition: background-color 0.2s;
    }

    &:hover {
      background-color: var(--surface-hover);
    }
    
    i {
      width: 1.25rem;
      margin-right: 0.75rem;
      text-align: center;
    }
  }

  /* Responsiveness */
  @media (max-width: 768px) {
    .navbar {
      position: sticky;
    }
  }

  /* Utility classes */
  .ml-2 {
    margin-left: 0.5rem;
  }

  .rotate-180 {
    transform: rotate(180deg);
  }

  /* Transitions */
  .transition-transform {
    transition: transform 0.2s ease;
  }

  /* Dark mode overrides */
  :root[data-theme='dark'] {
    .navbar .logo, .navbar .logo-container .logo, .mobile-menu-panel .mobile-logo {
      .light-logo,
      .mobile-light-logo {
        display: none;
      }

      .dark-logo,
      .mobile-dark-logo {
        display: block;
      }
    }

    .fallback-avatar {
      background-color: rgba(61, 25, 81, 0.2) !important;
      
      i {
        color: #C9A4E8 !important;
      }
    }
  }

  /* Define primary RGB values for rgba usage */
  :root {
    --primary-rgb: 61, 25, 81; /* #3D1951 */
  }

  :root[data-theme='dark'] {
    --primary-rgb: 201, 164, 232; /* #C9A4E8 — readable on #3D1951 plate */
  }

  /* Retain existing styles below this line */
  main {
    flex: 1;
    width: 100%;
    height: calc(100vh - 64px);
    overflow-y: auto;
    position: relative;
    z-index: 1;
  }

  // ... keep existing CSS for footer, etc ...

  .coming-soon-badge {
    background: none;
    color: #b0b8c1;
    font-size: 0.68rem;
    font-weight: 600;
    border-radius: 0;
    padding: 0;
    margin-left: 0;
    margin-top: 0.1em;
    letter-spacing: 0.01em;
    display: block;
    vertical-align: baseline;
    border: none;
    text-align: left;
  }
  .coming-soon-nav {
    cursor: not-allowed !important;
    user-select: none;
  }

  /* URGENT SYSTEM MAINTENANCE OVERLAY */
  .system-maintenance-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: linear-gradient(135deg, #3D1951 0%, #53246B 50%, #29B533 100%);
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  }

  .maintenance-message {
    text-align: center;
    max-width: 500px;
    padding: 2rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }

  .maintenance-icon {
    font-size: 4rem;
    margin-bottom: 1.5rem;
    color: #5A2A75;
    animation: pulse 2s infinite;
  }

  .maintenance-message h1 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  .maintenance-message p {
    font-size: 1.2rem;
    line-height: 1.6;
    margin-bottom: 1rem;
    color: rgba(255, 255, 255, 0.9);
  }

  .maintenance-subtitle {
    font-size: 1rem !important;
    color: rgba(255, 255, 255, 0.7) !important;
    font-style: italic;
  }

  .maintenance-spinner {
    margin-top: 2rem;

    .spinner {
      width: 50px;
      height: 50px;
      border: 4px solid rgba(255, 255, 255, 0.3);
      border-top: 4px solid #5A2A75;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto;
    }
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.8;
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  /* Responsive design for maintenance message */
  @media (max-width: 768px) {
    .maintenance-message {
      margin: 1rem;
      padding: 1.5rem;
    }

    .maintenance-message h1 {
      font-size: 2rem;
    }

    .maintenance-message p {
      font-size: 1rem;
    }

    .maintenance-icon {
      font-size: 3rem;
    }
  }
</style>